import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { v4 as uuidv4 } from 'uuid';
import { bmxurl, getAccess, selectableIdsToSave } from 'src/globals';
import { MoveBookingService } from './move-booking.service';

//A change to packages or time sets the assistant has worked out, ready to go on a card. Nothing
//is saved until staff press Confirm, which runs apply() - and apply() reads everything again
//first, since anything may have changed while the card was waiting.
export interface packageproposal {
  title: string;
  icon: string;
  workingText: string;
  summary: string;
  apply: () => Promise<string>;
}

//The days of the week as the start time records name them, Sunday first to match Date.getDay().
const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

//What staff call each visibility, and what the package saves.
const VISIBILITY: { [name: string]: string } = { hidden: 'NEITHER', public: 'PUBLIC', venue: 'VENUE', both: 'BOTH' };

//The AI staff assistant's package and time set tools - PROTOTYPE. Read by AssistantService,
//which puts any proposal on a card.
//
//Why a package does not show on a day is worked out the way the Dashboard itself decides it, as
//far as the Dashboard can see: the API's own answers come first - whether Make a Booking's list
//for that date (PackageGroupList) has the package, and what /Availability in diagnostic mode says
//about each time - and the package's settings are then read to explain them.
//
//Changes are saved the way the Dashboard's own screens save them:
//  - packages as Package Edit does (POST / PUT package, with its tidying before a save, below),
//    a copy as Copy Package does (the package posted again with originatingid set);
//  - start times as Start Times' "copy to package" does (POST packagestarttimes when the package
//    has none for that time set, PUT with the record's id when it has);
//  - group membership as Package Group Edit does (the whole group PUT with the package added);
//  - time set dates as Time Set Edit does (the whole time set PUT).
//Package Edit's own code is left alone; its save steps are repeated in prepareForSave().
@Injectable({
  providedIn: 'root'
})
export class PackageAssistantService {

  constructor(private http: HttpClient, private moveBookingService: MoveBookingService) { }

  private get venueId(): string {
    return localStorage.getItem('SmartVenueID') ?? '';
  }

  //#region Reading

  //One package in full, as staff would want it described.
  async describePackage(wanted: any): Promise<any> {
    const pkg = await this.fullPackage(wanted);
    const [sets, groups] = await Promise.all([this.timeSets(), this.groupsWith(pkg.packageId)]);

    const startTimes: any = {};

    for (const set of sets) {
      const record = await this.startTimesFor(pkg.packageId, set.id);
      startTimes[set.name + (set.isdefault ? ' (default)' : '')] = record == null ? 'none saved' : this.daysOf(record);
    }

    return this.compact({
      package_id: pkg.packageId,
      name: pkg.name,
      switched_off: pkg.disabled == true ? true : undefined,
      visibility: this.visibilityText(pkg.visibility),
      on_sale_from: this.dayText(pkg.startDate),
      on_sale_to: this.dayText(pkg.endDate),
      group_size: { min: pkg.min, max: pkg.max },
      length_minutes: this.packageLength(pkg),
      itinerary: (pkg.resources ?? []).map((res: any) => res.name + ' (' + res.length + ' min' + (res.mode == 'COMPLEX' ? ', chosen from a group' : '') + ')'),
      admissions: (pkg.admissions ?? []).map((adm: any) => this.compact({
        name: adm.name, price: adm.price, price_is: adm.fixedPrice ? 'per booking' : 'per person', min: adm.min, max: adm.max
      })),
      extras: (pkg.extras ?? []).filter((ext: any) => ext.disabled != true).map((ext: any) => this.compact({
        name: ext.name, price: this.extraPricing(ext),
        min: ext.min, max: ext.max, one_per_participant: ext.allparticipants == true ? true : undefined
      })),
      upgrades: (pkg.packageupgrades ?? []).filter((u: any) => u.disabled != true).map((u: any) => this.compact({
        name: u.name ?? u.upgrade?.name, price: u.price, per_person: u.perperson == true ? true : undefined
      })),
      admission_based_upgrades: (pkg.packageabupgrades ?? []).filter((u: any) => u.disabled != true).map((u: any) => this.compact({
        name: u.name ?? u.upgrade?.name, price: u.price, per_person: u.perperson == true ? true : undefined
      })),
      public_deposit_policy: pkg.publicDepositPolicy?.name,
      venue_deposit_policy: pkg.venueDepositPolicy?.name,
      confirmation: pkg.confirmation?.name,
      warning: pkg.warning?.name,
      questionnaire: pkg.questionnaire?.name,
      party_package: pkg.ispartypackage == true ? true : undefined,
      package_groups: groups.map(group => group.packagegroupname),
      booking_window: this.bookingWindow(pkg),
      start_times_by_time_set: startTimes
    });
  }

  //The venue's time sets, their dates, and any that overlap.
  async describeTimeSets(): Promise<any> {
    const sets = await this.timeSets();

    return {
      time_sets: sets.map(set => this.compact({
        time_set_id: set.id,
        name: set.name,
        default: set.isdefault == true ? true : undefined,
        dates: set.isdefault ? 'every date no other time set covers' :
          (set.holidayopenings ?? []).map((range: any) => this.dayText(range.dateFrom) + ' to ' + this.dayText(range.dateTo))
      })),
      no_default: sets.some(set => set.isdefault) ? undefined : 'There is no default time set, so dates no other time set covers have no times at all.',
      overlaps: this.overlaps(sets).map(item => item.a + ' and ' + item.b + ' both cover ' + this.dayText(item.from) + ' to ' + this.dayText(item.to))
    };
  }

  //What a new package can be made from.
  async buildingBlocks(): Promise<any> {
    const get = (url: string) => this.http.get(bmxurl + url).toPromise().then((response: any) => response?.data ?? []).catch(() => []);

    const [resources, admissions, deposits, confirmations, questionnaires, groups, fees, venueTimes, warnings, questions] = await Promise.all([
      get('ResourceListForPackage/' + this.venueId + '/false'),
      get('AdmissionList/' + this.venueId + '/false'),
      get('DepositPolicyList/' + this.venueId + '/false'),
      get('ConfirmationList/' + this.venueId),
      get('QuestionnaireList/' + this.venueId),
      get('PackageGroupListVenue/' + this.venueId + '/false'),
      get('BookingFeeList/' + this.venueId + '/false'),
      this.http.get(bmxurl + 'VenueStartTimes/' + this.venueId).toPromise().then((response: any) => response?.data).catch(() => null),
      get('WarningList/' + this.venueId + '/false'),
      get('bookingquestionlist/' + this.venueId + '/false')
    ]);

    const defaultFee = (fees as any[]).find(fee => fee.isdefault === true);
    const lists = await this.venueLists();

    return this.compact({
      extras: lists.extras.map(ext => this.compact({
        name: ext.name, price: this.extraPricing(ext), venue_only: ext.venueextras == true ? true : undefined
      })),
      upgrades: lists.upgrades.map(u => u.name),
      admission_based_upgrades: lists.abUpgrades.map(u => u.name),
      resources: (resources as any[]).filter(res => (res.eventid ?? '') == '' && res.disabled != true).map(res => this.compact({
        name: res.name, length_minutes: res.length, exclusive: res.exclusive == true ? true : undefined,
        group_of_resources: res.isresourcecontainer == true ? 'yes - can only be used by copying a package that has it, or on Package Edit' : undefined
      })),
      admissions: (admissions as any[]).filter(adm => adm.disabled != true).map(adm => this.compact({
        name: adm.name, price: adm.price, price_is: adm.fixedPrice ? 'per booking' : 'per person', min: adm.min, max: adm.max
      })),
      deposit_policies: (deposits as any[]).filter(dp => dp.disabled != true).map(dp => dp.name),
      confirmations: (confirmations as any[]).filter(item => item.disabled != true).map(item => item.name),
      questionnaires: (questionnaires as any[]).filter(item => item.disabled != true).map(item => item.name),
      booking_questions: (questions as any[]).filter(item => item.disabled != true).map(item => item.questiontext),
      warnings: (warnings as any[]).filter(item => item.disabled != true).map(item => item.name),
      package_groups: (groups as any[]).map(group => group.packagegroupname),
      default_booking_fee: defaultFee?.name,
      venue_start_times: venueTimes == null ? undefined : this.daysOf(venueTimes)
    });
  }

  //Everything that decides whether a package shows on a day, and what each time is doing.
  async diagnose(input: any): Promise<any> {
    const day = this.parseDay(input.date);

    if (day == null) {
      throw new Error('The date must be YYYY-MM-DD.');
    }

    const pkg = await this.fullPackage(input.package);
    const key = this.keyOfDay(day);
    const weekday = WEEKDAYS[day.getDay()];
    const findings: any[] = [];
    const add = (severity: string, finding: string, fix?: string) => findings.push(this.compact({ severity: severity, finding: finding, fix: fix }));

    //The package's own switches and dates.
    if (pkg.disabled == true) {
      add('blocks', 'The package is switched off.', 'update_package_settings with switch_on');
    }

    const visibility = (pkg.visibility ?? '').toString().toUpperCase();

    if (visibility == 'NEITHER') {
      add('blocks', 'The package is Hidden, so it is offered neither in Make a Booking nor on the public booking wizard.', 'update_package_settings visibility');
    }
    else if (visibility == 'PUBLIC') {
      add('blocks', 'The package is Public Bookings Only, so Make a Booking does not offer it (the public booking wizard does).', 'update_package_settings visibility');
    }
    else if (visibility == 'VENUE') {
      add('info', 'The package is Preview/Venue Only, so customers cannot book it on the public booking wizard - only staff, in Make a Booking.', 'update_package_settings visibility');
    }

    const fromKey = this.keyOfText(pkg.startDate);
    const toKey = this.keyOfText(pkg.endDate);

    if (fromKey != null && key < fromKey) {
      add('blocks', 'The package is only on sale from ' + this.dayText(pkg.startDate) + '.', 'update_package_settings start_date');
    }

    if (toKey != null && key > toKey) {
      add('blocks', 'The package stopped being on sale after ' + this.dayText(pkg.endDate) + '.', 'update_package_settings end_date');
    }

    this.windowFindings(pkg, day).forEach(item => add(item.severity, item.finding, item.fix));

    //Package groups: Make a Booking offers packages group by group, so one in no group is never offered.
    const [groups, listed] = await Promise.all([this.groupsWith(pkg.packageId), this.listedOn(pkg.packageId, day)]);

    if (groups.length == 0) {
      add('blocks', 'The package is not in any package group, and Make a Booking only offers packages through their groups.', 'add_package_to_group');
    }

    //Closings for the day.
    const venueClosings = await this.venueClosingsOn(day);

    venueClosings.forEach(closing => {
      add(closing.closedallday ? 'blocks' : 'warning', 'Venue closing "' + closing.name + '" covers this date' + (closing.closedallday ? ', all day.' : ' (part of the day).'));
    });

    (await this.resourceWindowsOn(pkg, day)).forEach(item => add(item.severity, item.finding));

    //The time set in force and its start times.
    const sets = await this.timeSets();
    const covering = sets.filter(set => !set.isdefault && this.setCovers(set, key));
    const fallback = sets.find(set => set.isdefault);
    let inForce: any = null;

    if (covering.length > 1) {
      add('blocks', 'Time sets ' + covering.map(set => '"' + set.name + '"').join(' and ') + ' both cover this date. Time sets must never overlap, so which one is used is not reliable.',
        'change_time_set_dates to take the date off all but one of them (ask staff which should apply)');
    }
    else if (covering.length == 1) {
      inForce = covering[0];
    }
    else if (fallback != null) {
      inForce = fallback;
    }
    else {
      add('blocks', 'No time set covers this date and there is no default time set, so there are no times at all.');
    }

    let times: string[] = null;

    if (inForce != null) {
      const record = await this.startTimesFor(pkg.packageId, inForce.id);

      if (record == null) {
        add('blocks', 'The package has no start times saved for time set "' + inForce.name + '", which is the one in force on this date.',
          'ask staff which start times they want for each day, then set_start_times for time set "' + inForce.name + '"');
      }
      else {
        times = this.timesOn(record, weekday);

        if (times.length == 0) {
          add('blocks', 'Time set "' + inForce.name + '" has no ' + this.weekdayName(weekday) + ' start times for this package.',
            'ask staff which ' + this.weekdayName(weekday) + ' times they want, then set_start_times');
        }
      }

      const venue = await this.venue();
      const opens = this.minutesOf(venue?.startTime);
      const closes = this.minutesOf(venue?.endTime);
      const length = this.packageLength(pkg);

      if (times != null && opens != null && closes != null) {
        times.forEach(time => {
          const start = this.minutesOf(time);

          if (start < opens || start >= closes) {
            add('warning', time + ' is outside the venue\'s opening hours (' + venue.startTime + ' to ' + venue.endTime + ').');
          }
          else if (length > 0 && start + length > closes) {
            add('warning', 'A ' + length + ' minute package starting at ' + time + ' would run past closing (' + venue.endTime + ').');
          }
        });
      }
    }

    //What the availability check makes of each time.
    const people = Math.max(Number(input.people) || Number(pkg.min) || 1, 1);
    const availability = await this.availabilityOn(pkg, day, people).catch(() => null);

    if (availability == null) {
      add('info', 'The availability check could not be run.');
    }
    else if (availability.length == 0 && (times ?? []).length > 0) {
      add('blocks', 'The availability check offered no times at all for ' + people + ' people, although start times are set.');
    }
    else if (availability.length > 0 && !availability.some(slot => slot.available)) {
      add('blocks', 'Every time is unavailable for ' + people + ' people - see the reason on each time.');
    }

    return this.compact({
      package: pkg.name,
      package_id: pkg.packageId,
      date: input.date,
      weekday: this.weekdayName(weekday),
      offered_in_make_a_booking_that_day: listed,
      time_set_in_force: inForce?.name,
      start_times_that_day: times ?? undefined,
      people_checked: people,
      findings: findings,
      availability: availability ?? undefined,
      note: 'offered_in_make_a_booking_that_day is the API\'s own list for that date. Each availability reason comes from the API.'
    });
  }

  //#endregion

  //#region Proposals

  //A package's start times for one time set: the weekdays given are replaced, the others kept.
  async proposeStartTimes(input: any): Promise<packageproposal> {
    await this.requireAccess();

    const pkg = await this.fullPackage(input.package);
    const set = await this.timeSet(input.time_set);
    const days = this.daysFrom(input.days);
    const current = await this.startTimesFor(pkg.packageId, set.id);
    const lines: string[] = [];

    Object.keys(days).forEach(weekday => {
      const was = current == null ? [] : this.timesOn(current, weekday);
      const now = days[weekday].map(item => item.time);

      if (was.join() != now.join()) {
        lines.push(this.weekdayName(weekday) + ': ' + (was.length > 0 ? was.join(', ') : 'none') + ' → ' + (now.length > 0 ? now.join(', ') : 'none'));
      }
    });

    if (lines.length == 0) {
      throw new Error('Those are the start times the package already has.');
    }

    return {
      title: 'Set start times',
      icon: 'pi-clock',
      workingText: 'Saving the start times...',
      summary: 'Start times for ' + pkg.name + ', time set "' + set.name + '"' + (current == null ? ' (none saved yet - days not listed will have no times)' : '') + ':\n' + lines.join('\n'),
      apply: async () => {
        await this.requireAccess();
        await this.saveStartTimes(pkg.packageId, set, days);
        return 'Start times saved.';
      }
    };
  }

  //Another package's start times copied onto a package, for one time set or all of them, as
  //Start Times' "copy to package" does. Time sets the other package has none for are left alone.
  async proposeCopyStartTimes(input: any): Promise<packageproposal> {
    await this.requireAccess();

    const from = await this.fullPackage(input.from_package);
    const to = await this.fullPackage(input.to_package);

    if (from.packageId == to.packageId) {
      throw new Error('That is the same package.');
    }

    const sets = input.time_set != null && input.time_set !== ''
      ? [this.pick(await this.timeSets(), input.time_set, s => s.id, s => s.name, 'time set')]
      : await this.timeSets();

    const copies: { set: any, days: any }[] = [];

    for (const set of sets) {
      const record = await this.startTimesFor(from.packageId, set.id);

      if (record != null) {
        copies.push({ set: set, days: this.allDays(record) });
      }
    }

    if (copies.length == 0) {
      throw new Error(from.name + ' has no start times saved for ' + (sets.length == 1 ? 'time set "' + sets[0].name + '"' : 'any time set') + '.');
    }

    return {
      title: 'Copy start times',
      icon: 'pi-clock',
      workingText: 'Saving the start times...',
      summary: 'Copy start times from ' + from.name + ' to ' + to.name + ', replacing what ' + to.name + ' has for these time sets:\n' +
        copies.map(copy => '"' + copy.set.name + '": ' + this.timesSummary(copy.days)).join('\n'),
      apply: async () => {
        await this.requireAccess();
        const problems: string[] = [];

        for (const copy of copies) {
          await this.saveStartTimes(to.packageId, copy.set, copy.days).catch(error => problems.push('"' + copy.set.name + '" (' + (error?.message ?? 'failed') + ')'));
        }

        if (problems.length == copies.length) {
          throw new Error('No start times saved: ' + problems.join('; '));
        }

        return 'Start times copied.' + (problems.length > 0 ? ' These did not save: ' + problems.join('; ') + '.' : '');
      }
    };
  }

  //Settings on a package that are safe to change on their own.
  async proposeSettings(input: any): Promise<packageproposal> {
    await this.requireAccess();

    const pkg = await this.fullPackage(input.package);
    const changes = input.changes ?? {};
    const lines: string[] = [];
    const apply: ((target: any) => void)[] = [];

    if (changes.switch_on === true) {
      if (pkg.disabled != true) {
        throw new Error('The package is already switched on.');
      }

      lines.push('Switch the package on');
      apply.push(target => target.disabled = false);
    }

    if (changes.visibility != null) {
      const value = VISIBILITY[(changes.visibility ?? '').toString().toLowerCase()];

      if (value == null) {
        throw new Error('visibility must be one of hidden, public, venue or both.');
      }

      lines.push('Visibility: ' + this.visibilityText(pkg.visibility) + ' → ' + this.visibilityText(value));
      apply.push(target => target.visibility = value);
    }

    const start = changes.start_date != null ? this.dayOrFail(changes.start_date, 'start_date') : null;
    const end = changes.end_date != null ? this.dayOrFail(changes.end_date, 'end_date') : null;
    const startKey = start != null ? this.keyOfDay(start) : this.keyOfText(pkg.startDate);
    const endKey = end != null ? this.keyOfDay(end) : this.keyOfText(pkg.endDate);

    if (startKey != null && endKey != null && endKey < startKey) {
      throw new Error('The end date cannot be before the start date.');
    }

    if (start != null) {
      lines.push('On sale from: ' + this.dayText(pkg.startDate) + ' → ' + this.dayText(changes.start_date));
      apply.push(target => target.startDate = this.wallClockDay(start));
    }

    if (end != null) {
      lines.push('On sale until: ' + this.dayText(pkg.endDate) + ' → ' + this.dayText(changes.end_date));
      apply.push(target => target.endDate = this.wallClockDay(end));
    }

    const min = changes.min_group != null ? Number(changes.min_group) : Number(pkg.min);
    const max = changes.max_group != null ? Number(changes.max_group) : Number(pkg.max);

    if (changes.min_group != null || changes.max_group != null) {
      if (isNaN(min) || isNaN(max) || min < 0 || max < 0 || max < min) {
        throw new Error('The group size must be whole numbers, with the maximum not less than the minimum.');
      }

      lines.push('Group size: ' + pkg.min + '-' + pkg.max + ' → ' + min + '-' + max);
      apply.push(target => { target.min = min; target.max = max; });
    }

    if (changes.can_book_today != null) {
      lines.push('Bookings for today: ' + (pkg.cannotmakebookingstoday ? 'not allowed' : 'allowed') + ' → ' + (changes.can_book_today ? 'allowed' : 'not allowed'));
      apply.push(target => target.cannotmakebookingstoday = changes.can_book_today != true);
    }

    if (changes.min_notice_minutes != null) {
      const value = Math.max(Number(changes.min_notice_minutes) || 0, 0);
      lines.push('Minimum notice: ' + (pkg.bookingscannotbemadewithinxminutes ?? 0) + ' → ' + value + ' minutes');
      apply.push(target => target.bookingscannotbemadewithinxminutes = value);
    }

    if (changes.max_days_ahead != null) {
      const value = Math.max(Number(changes.max_days_ahead) || 0, 0);
      lines.push('Furthest ahead: ' + this.daysAheadText(pkg.bookingscannotbemadeafterxminutes) + ' → ' + (value == 0 ? 'no limit' : value + ' days'));
      apply.push(target => target.bookingscannotbemadeafterxminutes = value * 1440);
    }

    if (changes.calendar_months != null) {
      const value = Math.max(Number(changes.calendar_months) || 0, 1);
      lines.push('Months shown on the booking calendar: ' + (pkg.maxcalendarmonths || 3) + ' → ' + value);
      apply.push(target => target.maxcalendarmonths = value);
    }

    //The venue's own items a package points to, chosen by name. "none" clears the optional ones.
    const choose = async (wanted: any, label: string, listUrl: string, idOf: (x: any) => any, current: any, field: string, optional: boolean) => {
      if (wanted == null || wanted === '') {
        return;
      }

      if (optional && wanted.toString().toLowerCase() == 'none') {
        lines.push(label + ': ' + (current?.name ?? 'none') + ' → none');
        apply.push(target => target[field] = null);
        return;
      }

      const response: any = await this.http.get(bmxurl + listUrl).toPromise();
      const items = ((Array.isArray(response) ? response : response?.data) ?? []).filter((item: any) => item.disabled != true);
      const item = this.pick(items, wanted, idOf, (x: any) => x.name, label.toLowerCase());

      lines.push(label + ': ' + (current?.name ?? 'none') + ' → ' + item.name);
      apply.push(target => target[field] = item);
    };

    await choose(changes.public_deposit_policy, 'Public deposit policy', 'DepositPolicyList/' + this.venueId + '/false', x => x.id, pkg.publicDepositPolicy, 'publicDepositPolicy', false);
    await choose(changes.venue_deposit_policy, 'Venue deposit policy', 'DepositPolicyList/' + this.venueId + '/false', x => x.id, pkg.venueDepositPolicy, 'venueDepositPolicy', false);
    await choose(changes.confirmation, 'Confirmation', 'ConfirmationList/' + this.venueId, x => x.confirmationid ?? x.id, pkg.confirmation, 'confirmation', true);
    await choose(changes.warning, 'Warning', 'WarningList/' + this.venueId + '/false', x => x.warningid ?? x.id, pkg.warning, 'warning', true);
    await choose(changes.questionnaire, 'Questionnaire', 'QuestionnaireList/' + this.venueId, x => x.questionnaireid ?? x.id, pkg.questionnaire, 'questionnaire', true);

    if (lines.length == 0) {
      throw new Error('No changes given.');
    }

    return {
      title: 'Change package settings',
      icon: 'pi-box',
      workingText: 'Saving the package...',
      summary: 'Change ' + pkg.name + ':\n' + lines.join('\n'),
      apply: async () => {
        await this.requireAccess();

        //Read again, so only these settings change and nothing saved since is lost.
        const fresh = await this.fullPackage(pkg.packageId);
        apply.forEach(change => change(fresh));
        this.prepareForSave(fresh);

        const response: any = await this.http.put(bmxurl + '/package', fresh).toPromise();

        if (!response?.succeeded) {
          throw new Error(response?.messages?.[0] ?? 'The package could not be saved.');
        }

        return 'Package saved.';
      }
    };
  }

  //#endregion

  //#region Creating the venue's own items
  //Each made on its own, the way its own screen makes it, and not tied to any package. The item's
  //screen's access is needed to confirm.

  //A new extra, as Extra Edit saves one.
  async proposeCreateExtra(input: any): Promise<packageproposal> {
    await this.requireAccess('EXTRAS', 'Extras', 'extras');

    const name = (input.name ?? '').toString().trim();
    const description = (input.description ?? '').toString().trim();

    if (name == '' || description == '') {
      throw new Error('A new extra needs a name and a description.');
    }

    await this.refuseDuplicate('ExtraList/' + this.venueId + '/true', name, 'an extra');

    const variable = input.variable_value == true;
    const price = variable ? 0 : Number(input.price);

    if (!variable && (input.price == null || isNaN(price) || price < 0)) {
      throw new Error('A new extra needs a price (0 or more), unless staff enter its value on each booking (variable_value).');
    }

    const extra: any = {
      id: null, extraId: null, name: name, description: description, disabled: false, venueId: this.venueId,
      monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: true, sunday: true,
      extraOptions: [], tags: [], price: price,
      //The Extras screen's "Per Extra or per Booking?" - true is Per Booking.
      perPerson: input.charged == 'per_booking',
      question: (input.question ?? '').toString().trim(), answer: '',
      //A variable value extra is always venue only, as Extra Edit makes it.
      venueextras: variable || input.venue_only == true,
      imageurl: '', imageguid: uuidv4(), datetype: 'ALLDAYS', singledate: '1900-01-01T00:00:00.0000Z', variablevalue: variable
    };

    let when = 'all days';

    if (input.single_date != null && input.single_date !== '') {
      const day = this.dayOrFail(input.single_date, 'single_date');
      extra.datetype = 'SINGLEDATE';
      extra.singledate = this.wallClockDay(day).replace('.000Z', '.0000Z');
      when = 'only on ' + this.dayText(input.single_date);
    }
    else if (Array.isArray(input.days) && input.days.length > 0) {
      const days = input.days.map((day: any) => (day ?? '').toString().trim().toLowerCase());
      const wrong = days.filter((day: string) => !WEEKDAYS.includes(day));

      if (wrong.length > 0) {
        throw new Error('"' + wrong.join('", "') + '" is not a day of the week.');
      }

      extra.datetype = 'CHOOSEDAYS';
      WEEKDAYS.forEach(day => extra[day] = days.includes(day));
      when = 'on ' + WEEKDAYS.filter(day => extra[day]).map(day => this.weekdayName(day)).join(', ');
    }

    const lines = [
      'New extra ' + name,
      description,
      'Price: ' + this.extraPricing(extra),
      'Available: ' + when,
      extra.venueextras ? 'Venue only - not offered on the public booking wizard' : 'Offered to customers and staff',
      extra.question != '' ? 'Asks: ' + extra.question : null
    ].filter(line => line != null);

    return {
      title: 'Create extra',
      icon: 'pi-shopping-bag',
      workingText: 'Creating the extra...',
      summary: lines.join('\n'),
      apply: async () => {
        await this.requireAccess('EXTRAS', 'Extras', 'extras');
        await this.refuseDuplicate('ExtraList/' + this.venueId + '/true', name, 'an extra');

        const response: any = await this.http.post(bmxurl + '/extra', extra).toPromise();

        if (!response?.succeeded) {
          throw new Error(response?.messages?.[0] ?? 'The extra could not be created.');
        }

        return 'Extra "' + name + '" created. It can now be put on packages (change_package_extras).';
      }
    };
  }

  //A new deposit policy, as Deposit Policies Edit saves one.
  async proposeCreateDepositPolicy(input: any): Promise<packageproposal> {
    await this.requireAccess('DEPOSITPOLICIES', 'Deposit Policies', 'deposit policies');

    const name = (input.name ?? '').toString().trim();

    if (name == '') {
      throw new Error('A new deposit policy needs a name.');
    }

    await this.refuseDuplicate('DepositPolicyList/' + this.venueId + '/true', name, 'a deposit policy');

    const types: { [name: string]: string } = { none: 'NoDeposit', fixed: 'Fixed', percentage: 'Percentage', per_person: 'PerPerson' };
    const type = types[(input.type ?? '').toString().toLowerCase()];

    if (type == null) {
      throw new Error('type must be none, fixed, percentage or per_person.');
    }

    const amount = type == 'NoDeposit' ? 0 : Number(input.amount);

    if (type != 'NoDeposit' && (input.amount == null || isNaN(amount) || amount < 0 || (type == 'Percentage' && amount > 100))) {
      throw new Error(type == 'Percentage' ? 'A percentage deposit needs a percentage from 0 to 100.' : 'This deposit needs an amount, 0 or more.');
    }

    const policy: any = {
      id: null, name: name, venueid: this.venueId, disabled: false, alternativeamount: 0, alternativeamountthreshold: 0,
      type: type, amount: amount, includeextras: input.must_cover_extras == true, allowfullpayment: input.allow_full_payment == true
    };

    //Only Fixed and Percentage policies can take a different deposit closer to the start date.
    if (input.closer_amount != null || input.closer_days != null) {
      if (type != 'Fixed' && type != 'Percentage') {
        throw new Error('Only fixed and percentage deposits can use a different deposit closer to the start date.');
      }

      const closerAmount = Number(input.closer_amount);
      const closerDays = Number(input.closer_days);

      if (isNaN(closerAmount) || closerAmount < 0 || isNaN(closerDays) || closerDays <= 0) {
        throw new Error('A different deposit closer to the start date needs both closer_amount and closer_days (more than 0).');
      }

      policy.alternativeamount = closerAmount;
      policy.alternativeamountthreshold = Math.round(closerDays);
    }

    const amountText = (value: number) => type == 'Percentage' ? value + '%' : value.toFixed(2) + (type == 'PerPerson' ? ' per person' : '');
    const lines = [
      'New deposit policy ' + name,
      'Deposit: ' + (type == 'NoDeposit' ? 'none' : amountText(amount)),
      policy.alternativeamountthreshold > 0 ? 'Within ' + policy.alternativeamountthreshold + ' days of the start date: ' + amountText(policy.alternativeamount) : null,
      'Deposit must cover the full cost of any extras: ' + (policy.includeextras ? 'yes' : 'no'),
      'Customer can pay in full: ' + (policy.allowfullpayment ? 'yes' : 'no')
    ].filter(line => line != null);

    return {
      title: 'Create deposit policy',
      icon: 'pi-wallet',
      workingText: 'Creating the deposit policy...',
      summary: lines.join('\n'),
      apply: async () => {
        await this.requireAccess('DEPOSITPOLICIES', 'Deposit Policies', 'deposit policies');
        await this.refuseDuplicate('DepositPolicyList/' + this.venueId + '/true', name, 'a deposit policy');

        const response: any = await this.http.post(bmxurl + '/depositpolicy', policy).toPromise();

        if (!response?.succeeded) {
          throw new Error(response?.messages?.[0] ?? 'The deposit policy could not be created.');
        }

        return 'Deposit policy "' + name + '" created.';
      }
    };
  }

  //A new admission, as Admission Edit saves one.
  async proposeCreateAdmission(input: any): Promise<packageproposal> {
    await this.requireAccess('ADMISSIONS', 'Admissions', 'admissions');

    const name = (input.name ?? '').toString().trim();
    const wizardText = (input.booking_wizard_text ?? '').toString().trim();

    if (name == '' || wizardText == '') {
      throw new Error('A new admission needs a name and its booking wizard text (what customers see when choosing it).');
    }

    await this.refuseDuplicate('AdmissionList/' + this.venueId + '/true', name, 'an admission');

    const price = Number(input.price);
    const min = Number(input.min ?? 0);
    const max = Number(input.max ?? 0);

    if (input.price == null || isNaN(price) || price < 0) {
      throw new Error('A new admission needs a price, 0 or more.');
    }

    if (isNaN(min) || isNaN(max) || min < 0 || max < min) {
      throw new Error('The maximum cannot be less than the minimum.');
    }

    let admissionClass = null;

    if (input.class != null && input.class !== '') {
      const response: any = await this.http.get(bmxurl + 'ClassList/' + this.venueId + '/false').toPromise();
      admissionClass = this.pick((response?.data ?? []) as any[], input.class, c => c.classID ?? c.id, c => c.name, 'class');
    }

    const admission: any = {
      id: null, name: name, bookingwizardtext: wizardText, fixedPrice: false, participant: input.participant !== false,
      venueid: this.venueId, admissionid: null, noofpeople: 0, price: price, admissionClass: admissionClass, min: min, max: max,
      index: 0, disabled: false, mustinclude: input.must_include == true, classid: null, eventtickettype: null, counter: [],
      uselinkeddiscounts: false, linkedadmissions: [], linkeddiscounts: [], uselinkedadmissions: false, maxlinkeddiscounts: 1,
      maxlinkedadmissions: 1, countstowardsmax: true, countstowardsmin: true, message: '', fixed: false, onlysetmax: false
    };

    const lines = [
      'New admission ' + name,
      'Booking wizard text: ' + wizardText,
      'Price: ' + price.toFixed(2) + ' each',
      'Minimum ' + min + ', maximum ' + max,
      'Participant: ' + (admission.participant ? 'yes' : 'no') + ', must include: ' + (admission.mustinclude ? 'yes' : 'no'),
      admissionClass != null ? 'Class: ' + admissionClass.name : null
    ].filter(line => line != null);

    return {
      title: 'Create admission',
      icon: 'pi-ticket',
      workingText: 'Creating the admission...',
      summary: lines.join('\n'),
      apply: async () => {
        await this.requireAccess('ADMISSIONS', 'Admissions', 'admissions');
        await this.refuseDuplicate('AdmissionList/' + this.venueId + '/true', name, 'an admission');

        const response: any = await this.http.post(bmxurl + '/admission', admission).toPromise();

        if (!response?.succeeded) {
          throw new Error(response?.messages?.[0] ?? 'The admission could not be created.');
        }

        return 'Admission "' + name + '" created. It can now be put on packages (change_package_admissions).';
      }
    };
  }

  //A new warning (a message shown when a package is booked), as Warning Edit saves one - with a
  //public message, a venue message, or both. Warning Edit checks the CLASSES access, so this does.
  async proposeCreateWarning(input: any): Promise<packageproposal> {
    await this.requireAccess('CLASSES', 'Warnings', 'warnings');

    const name = (input.name ?? '').toString().trim();
    const publicText = (input.public_message ?? '').toString().trim();
    const venueText = (input.venue_message ?? '').toString().trim();

    if (name == '') {
      throw new Error('A new warning needs a name.');
    }

    if (publicText == '' && venueText == '') {
      throw new Error('A warning needs a public message (shown to customers), a venue message (shown to staff), or both.');
    }

    await this.refuseDuplicate('WarningList/' + this.venueId + '/true', name, 'a warning');

    const warning: any = {
      id: null, warningid: null, name: name, venueid: this.venueId, disabled: false,
      venuemessage: this.asHtml(venueText), publicmessage: this.asHtml(publicText)
    };

    const lines = [
      'New warning ' + name,
      publicText != '' ? 'Shown to customers: ' + publicText : null,
      venueText != '' ? 'Shown to staff: ' + venueText : null
    ].filter(line => line != null);

    return {
      title: 'Create warning',
      icon: 'pi-exclamation-triangle',
      workingText: 'Creating the warning...',
      summary: lines.join('\n'),
      apply: async () => {
        await this.requireAccess('CLASSES', 'Warnings', 'warnings');
        await this.refuseDuplicate('WarningList/' + this.venueId + '/true', name, 'a warning');

        const response: any = await this.http.post(bmxurl + '/warning', warning).toPromise();

        if (!response?.succeeded) {
          throw new Error(response?.messages?.[0] ?? 'The warning could not be created.');
        }

        return 'Warning "' + name + '" created.';
      }
    };
  }

  //A new upgrade, as Upgrade Edit saves one: a name, a description and the resources it books,
  //each run one after the other as Upgrade Edit adds a plain resource. A resource that is a group
  //of resources needs Upgrade Edit's own choices, so it is left to that screen.
  async proposeCreateUpgrade(input: any): Promise<packageproposal> {
    await this.requireAccess('PACKAGE', 'Upgrades', 'upgrades');

    const name = (input.name ?? '').toString().trim();
    const description = (input.description ?? '').toString().trim();

    if (name == '' || description == '') {
      throw new Error('A new upgrade needs a name and a description.');
    }

    if (!Array.isArray(input.resources) || input.resources.length == 0) {
      throw new Error('An upgrade needs at least one resource, each with a length in minutes. package_building_blocks lists the venue\'s resources.');
    }

    await this.refuseDuplicate('UpgradeListall/' + this.venueId + '/true', name, 'an upgrade');

    const listed: any = await this.http.get(bmxurl + 'ResourceListForPackage/' + this.venueId + '/false').toPromise();
    const usable = ((listed?.data ?? []) as any[]).filter(res => (res.eventid ?? '') == '' && res.disabled != true);

    const resources = input.resources.map((item: any, index: number) => {
      const res = this.pick(usable, item.resource, r => r.resourceId, r => r.name, 'resource');

      if (res.isresourcecontainer) {
        throw new Error(res.name + ' is a group of resources, which needs Upgrade Edit\'s own settings - add it there.');
      }

      const length = item.length_minutes != null ? Number(item.length_minutes) : Number(res.length);

      if (!(length > 0)) {
        throw new Error(res.name + ' needs a length of more than 0 minutes.');
      }

      return {
        groupname: '', id: uuidv4(), resourceId: res.resourceId, name: res.name, useClasses: res.useClasses, exclusive: res.exclusive,
        eventid: res.eventid, concurrentUses: res.concurrentUses, maxConcurrent: res.maxConcurrent, disabled: res.disabled,
        venueId: res.venueId, colour: res.colour, orderindex: index, length: length, classes: res.classes,
        showonbooking: res.showonbooking, resources: res.resources ?? [], isresourcecontainer: res.isresourcecontainer,
        isconstructed: res.isconstructed, price: res.price, fixedprice: res.fixedprice, resourcegroupname: res.resourcegroupname,
        tags: res.tags, resourcegroup: res.resourcegroup, selectionmode: '', selectableresourcestrings: res.selectableresourcestrings,
        startwhen: '', startTimeOffset: res.startTimeOffset, optional: res.optional, quantityselector: 'GROUPSIZE',
        quantity: res.quantity, mode: 'SIMPLE', inline: true, ignore: false, admissions: null, inProgress: true,
        originatingid: '', wasoptional: false, wasselected: false, isupgrade: true, packageupgradeid: res.packageupgradeid,
        showinitinerary: true, showondaysheet: true, cansplit: false
      };
    });

    const upgrade: any = {
      id: null, upgradeid: null, name: name, description: description, imageurl: '', imageguid: uuidv4(),
      disabled: false, venueid: this.venueId, resources: resources
    };

    return {
      title: 'Create upgrade',
      icon: 'pi-star',
      workingText: 'Creating the upgrade...',
      summary: 'New upgrade ' + name + '\n' + description + '\nBooks: ' + resources.map((res: any) => res.name + ' ' + res.length + ' min').join(', ') +
        '\nIts price is set on each package it is put on.',
      apply: async () => {
        await this.requireAccess('PACKAGE', 'Upgrades', 'upgrades');
        await this.refuseDuplicate('UpgradeListall/' + this.venueId + '/true', name, 'an upgrade');

        const response: any = await this.http.post(bmxurl + '/Upgrade', upgrade).toPromise();

        if (!response?.succeeded) {
          throw new Error(response?.messages?.[0] ?? 'The upgrade could not be created.');
        }

        return 'Upgrade "' + name + '" created. It can now be put on packages with a price (change_package_upgrades).';
      }
    };
  }

  //A new booking question, as Booking Question Edit saves one. Multi-choice questions carry
  //their answers; Birthday and Register questions their own options.
  async proposeCreateQuestion(input: any): Promise<packageproposal> {
    await this.requireAccess('BOOKINGQUESTIONS', 'Booking Questions', 'booking questions');

    const types: { [name: string]: string } = {
      free_text: 'FREETEXT', single_choice: 'MULTICHOICESINGLE', multiple_choice: 'MULTICHOICEMULTIPLE', date: 'DATE',
      number: 'NUMBER', age: 'AGE', allergy: 'ALLERGY', birthday: 'BIRTHDAY', register: 'REGISTER'
    };
    const typeNames: { [code: string]: string } = {
      FREETEXT: 'Free Text', MULTICHOICESINGLE: 'Multi-choice single choice', MULTICHOICEMULTIPLE: 'Multi-choice multiple choice',
      DATE: 'Date', NUMBER: 'Number', AGE: 'Age', ALLERGY: 'Allergy Information', BIRTHDAY: 'Birthday Personal Details', REGISTER: 'Register'
    };

    const text = (input.question ?? '').toString().trim();
    const type = types[(input.type ?? '').toString().toLowerCase()];

    if (text == '') {
      throw new Error('A new booking question needs its wording.');
    }

    if (type == null) {
      throw new Error('type must be one of ' + Object.keys(types).join(', ') + '.');
    }

    await this.refuseDuplicate('bookingquestionlist/' + this.venueId + '/true', text, 'a booking question', q => q.questiontext);

    const question: any = {
      id: null, bookingquestionid: null, questiontext: text, listorder: 0, listitems: [], questiontype: type, warningtext: '',
      mandatory: input.mandatory == true, disabled: false, venueid: this.venueId, showname: true, showage: false, showdob: false,
      showgender: false, showinterests: false, linkedtoparticipants: false, answers: null, minage: 0, maxage: 0
    };

    const lines = ['New booking question: ' + text, 'Type: ' + typeNames[type]];

    if (type == 'MULTICHOICESINGLE' || type == 'MULTICHOICEMULTIPLE') {
      const choices = (Array.isArray(input.choices) ? input.choices : []).map((c: any) => (c ?? '').toString().trim()).filter((c: string) => c != '');

      if (choices.length < 2) {
        throw new Error('A multi-choice question needs at least two choices.');
      }

      question.listitems = choices.map((choice: string) => ({ id: uuidv4(), listitemtext: choice }));
      lines.push('Choices: ' + choices.join(', '));
    }

    //The people questions: a Register is a line per person on the booking, a Birthday the
    //birthday child's details. Each can ask date of birth and age, and be linked to participants.
    if (type == 'BIRTHDAY' || type == 'REGISTER') {
      question.showdob = input.ask_date_of_birth == true;
      question.showage = input.ask_age == true;
      question.linkedtoparticipants = input.linked_to_participants == true;

      if (question.showage) {
        question.minage = Math.max(Number(input.min_age_months) || 0, 0);
        question.maxage = Math.max(Number(input.max_age_months) || 0, 0);
      }

      if (type == 'REGISTER' && (input.extra_field_title ?? '').toString().trim() != '') {
        question.showinterests = true;
        question.warningtext = input.extra_field_title.toString().trim();
      }

      lines.push(['Name', question.showdob ? 'date of birth' : null, question.showage ? 'age' : null, question.showinterests ? question.warningtext : null]
        .filter(part => part != null).join(', ') + (question.linkedtoparticipants ? ' - one for each participant' : ''));
    }

    lines.push(question.mandatory ? 'Must be answered' : 'Optional');

    return {
      title: 'Create booking question',
      icon: 'pi-question-circle',
      workingText: 'Creating the question...',
      summary: lines.join('\n'),
      apply: async () => {
        await this.requireAccess('BOOKINGQUESTIONS', 'Booking Questions', 'booking questions');
        await this.refuseDuplicate('bookingquestionlist/' + this.venueId + '/true', text, 'a booking question', q => q.questiontext);

        const response: any = await this.http.post(bmxurl + '/bookingquestion', question).toPromise();

        if (!response?.succeeded) {
          throw new Error(response?.messages?.[0] ?? 'The question could not be created.');
        }

        return 'Booking question created. It can now go in a questionnaire (create_questionnaire).';
      }
    };
  }

  //A new questionnaire - the set of booking questions a package asks - as Booking Questionnaire
  //Edit saves one, from the venue's existing questions in the order given.
  async proposeCreateQuestionnaire(input: any): Promise<packageproposal> {
    await this.requireAccess('BOOKINGQUESTIONNAIRE', 'Booking Questionnaires', 'questionnaires');

    const name = (input.name ?? '').toString().trim();

    if (name == '') {
      throw new Error('A new questionnaire needs a name.');
    }

    if (!Array.isArray(input.questions) || input.questions.length == 0) {
      throw new Error('A questionnaire needs at least one question. Make any new ones first with create_booking_question.');
    }

    await this.refuseDuplicate('questionnairelist/' + this.venueId + '/true', name, 'a questionnaire');

    const pickQuestions = async () => {
      const response: any = await this.http.get(bmxurl + 'bookingquestionlist/' + this.venueId + '/false').toPromise();
      const all = (response?.data ?? []) as any[];
      const picked = input.questions.map((wanted: any) => this.pick(all, wanted, q => q.bookingquestionid, q => q.questiontext, 'booking question'));

      if (picked.some((q: any, index: number) => picked.findIndex((other: any) => other.bookingquestionid == q.bookingquestionid) != index)) {
        throw new Error('The same question is given twice.');
      }

      picked.forEach((q: any, index: number) => q.listorder = index);
      return picked;
    };

    const questions = await pickQuestions();

    return {
      title: 'Create questionnaire',
      icon: 'pi-list',
      workingText: 'Creating the questionnaire...',
      summary: 'New questionnaire ' + name + ':\n' + questions.map((q: any, index: number) => (index + 1) + '. ' + q.questiontext).join('\n'),
      apply: async () => {
        await this.requireAccess('BOOKINGQUESTIONNAIRE', 'Booking Questionnaires', 'questionnaires');
        await this.refuseDuplicate('questionnairelist/' + this.venueId + '/true', name, 'a questionnaire');

        const questionnaire = {
          id: null, bookingid: null, questionnaireid: null, venueid: this.venueId, name: name,
          bookingquestions: await pickQuestions(), disabled: false
        };

        const response: any = await this.http.post(bmxurl + '/questionnaire', questionnaire).toPromise();

        if (!response?.succeeded) {
          throw new Error(response?.messages?.[0] ?? 'The questionnaire could not be created.');
        }

        return 'Questionnaire "' + name + '" created. It can be chosen for a package when one is created.';
      }
    };
  }

  //A new confirmation (the email a customer gets for a booking), as the Confirmation editor saves
  //one. Customer services details default to the venue's, as that editor fills them in.
  async proposeCreateConfirmation(input: any): Promise<packageproposal> {
    await this.requireAccess('CONFIRMATIONS', 'Confirmations', 'confirmations');

    const name = (input.name ?? '').toString().trim();
    const subject = (input.email_subject ?? '').toString().trim();

    if (name == '' || subject == '') {
      throw new Error('A new confirmation needs a name and an email subject.');
    }

    await this.refuseDuplicate('ConfirmationList/' + this.venueId, name, 'a confirmation');

    const message = (input.message ?? '').toString().trim();
    const paymentText = (input.payment_message ?? '').toString().trim();
    const findText = (input.how_to_find_us ?? '').toString().trim();
    const mapLink = (input.map_link ?? '').toString().trim();

    const paymentTypes: { [name: string]: string } = { none: 'NONE', text: 'TEXTONLY', text_with_pay_button: 'TEXTONLYOB' };
    const paymentType = paymentText == '' ? 'NONE' : paymentTypes[(input.payment_message_type ?? 'text').toString().toLowerCase()];

    if (paymentType == null) {
      throw new Error('payment_message_type must be text or text_with_pay_button.');
    }

    const showEmail = input.show_email == true;
    const showPhone = input.show_phone == true;

    const confirmation: any = {
      id: null, confirmationid: null, name: name, subject: subject, disabled: false, venueid: this.venueId, emailsubject: '',
      showconfirmationmessage: message != '', confirmationmessage: this.asHtml(message),
      paymentmessagetype: paymentType, paymentmessage: paymentType == 'NONE' ? '' : this.asHtml(paymentText),
      howtofindustype: findText == '' ? 'NONE' : (mapLink != '' ? 'TEXTONLYLINK' : 'TEXTONLY'),
      howtofindustext: this.asHtml(findText), webaddressformap: findText == '' ? '' : mapLink,
      showcustomerservices: showEmail || showPhone, showemail: showEmail,
      emailtext: showEmail ? (input.email ?? localStorage.getItem('CustomerServiceEmail') ?? '').toString() : '',
      showphone: showPhone,
      phonetext: showPhone ? (input.phone ?? localStorage.getItem('CustomerServicePhone') ?? '').toString() : '',
      showtandcslink: false, showendtime: input.show_end_time == true,
      showitinerary: input.show_itinerary == true, originatingid: null, showlogo: input.show_logo == true
    };

    if (showEmail && confirmation.emailtext == '' || showPhone && confirmation.phonetext == '') {
      throw new Error('The venue has no customer services email or phone in its settings, so give the one to show.');
    }

    const yes = (flag: boolean) => flag ? 'yes' : 'no';
    const lines = [
      'New confirmation ' + name,
      'Email subject: ' + subject,
      message != '' ? 'Message: ' + message : null,
      paymentType != 'NONE' ? 'Payment message' + (paymentType == 'TEXTONLYOB' ? ' with pay button (only when money is owed)' : '') + ': ' + paymentText : null,
      findText != '' ? 'How to find us: ' + findText + (mapLink != '' ? ' (map: ' + mapLink + ')' : '') : null,
      showEmail || showPhone ? 'Customer services: ' + [showEmail ? confirmation.emailtext : null, showPhone ? confirmation.phonetext : null].filter(x => x != null).join(', ') : null,
      'Logo ' + yes(confirmation.showlogo) + ', itinerary ' + yes(confirmation.showitinerary) + ', end time ' + yes(confirmation.showendtime)
    ].filter(line => line != null);

    return {
      title: 'Create confirmation',
      icon: 'pi-envelope',
      workingText: 'Creating the confirmation...',
      summary: lines.join('\n'),
      apply: async () => {
        await this.requireAccess('CONFIRMATIONS', 'Confirmations', 'confirmations');
        await this.refuseDuplicate('ConfirmationList/' + this.venueId, name, 'a confirmation');

        const response: any = await this.http.post(bmxurl + '/Confirmations', confirmation).toPromise();

        if (!response?.succeeded) {
          throw new Error(response?.messages?.[0] ?? 'The confirmation could not be created.');
        }

        return 'Confirmation "' + name + '" created. It can now be set on packages (update_package_settings confirmation).';
      }
    };
  }

  //A new resource (a room, lane, arena...), as Resource Edit saves a plain one. A resource made up
  //of other resources, or one using classes, needs that screen's own settings, so is left to it.
  async proposeCreateResource(input: any): Promise<packageproposal> {
    await this.requireAccess('RESOURCES', 'Resources', 'resources');

    const name = (input.name ?? '').toString().trim();
    const capacity = Number(input.capacity);

    if (name == '') {
      throw new Error('A new resource needs a name.');
    }

    if (!(capacity > 0)) {
      throw new Error('A new resource needs its capacity (concurrent uses), more than 0.');
    }

    await this.refuseDuplicate('ResourceList/' + this.venueId + '//true', name, 'a resource');

    const colour = (input.colour ?? '').toString().trim();

    if (colour != '' && !/^#?[0-9a-f]{6}$/i.test(colour)) {
      throw new Error('colour must be a hex colour such as #3366CC.');
    }

    const resource: any = {
      groupname: '', id: null, resourceId: null, name: name, useClasses: false, exclusive: input.exclusive == true, eventid: null,
      concurrentUses: capacity, maxConcurrent: Math.max(Number(input.max_at_same_time) || 0, 0), disabled: false,
      venueId: this.venueId, colour: colour == '' ? '' : (colour.startsWith('#') ? colour : '#' + colour), orderindex: 0, length: 0,
      classes: [], showonbooking: input.show_on_bookings_made_today == true, resources: [], isresourcecontainer: false,
      isconstructed: false, price: 0, fixedprice: true, resourcegroupname: null, tags: null, resourcegroup: null,
      selectionmode: '', selectableresourcestrings: null, startwhen: '', startTimeOffset: 0, optional: false, quantityselector: '',
      quantity: 0, mode: '', inline: false, ignore: false, admissions: null, inProgress: true, originatingid: '',
      wasoptional: false, wasselected: false, isupgrade: false, packageupgradeid: null,
      showinitinerary: input.show_in_itinerary !== false, showondaysheet: input.show_on_day_sheet !== false,
      cansplit: input.can_split == true, daysheetinterval: Math.max(Number(input.day_sheet_interval) || 0, 0)
    };

    const yes = (flag: boolean) => flag ? 'yes' : 'no';
    const lines = [
      'New resource ' + name,
      'Capacity: ' + capacity + (resource.maxConcurrent > 0 ? ', at most ' + resource.maxConcurrent + ' uses at the same time' : ''),
      'Exclusive: ' + yes(resource.exclusive) + ', can split: ' + yes(resource.cansplit),
      'Shown in itinerary: ' + yes(resource.showinitinerary) + ', on the day sheet: ' + yes(resource.showondaysheet),
      resource.colour != '' ? 'Colour: ' + resource.colour : null
    ].filter(line => line != null);

    return {
      title: 'Create resource',
      icon: 'pi-building',
      workingText: 'Creating the resource...',
      summary: lines.join('\n'),
      apply: async () => {
        await this.requireAccess('RESOURCES', 'Resources', 'resources');
        await this.refuseDuplicate('ResourceList/' + this.venueId + '//true', name, 'a resource');

        const response: any = await this.http.post(bmxurl + '/Resource', resource).toPromise();

        if (!response?.succeeded) {
          throw new Error(response?.messages?.[0] ?? 'The resource could not be created.');
        }

        return 'Resource "' + name + '" created. It can now be used in packages and upgrades.';
      }
    };
  }

  //Plain text as the rich text editors (PrimeNG's p-editor) hold it: a paragraph per line, with
  //anything that looks like markup shown as text rather than run.
  private asHtml(text: string): string {
    if ((text ?? '') == '') {
      return '';
    }

    const escape = (line: string) => line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return text.split(/\r?\n/).map(line => '<p>' + (line.trim() == '' ? '<br>' : escape(line)) + '</p>').join('');
  }

  //Refuses a name the venue already uses for that kind of item, switched off ones included.
  private async refuseDuplicate(listUrl: string, name: string, what: string, nameOf: (item: any) => any = item => item.name): Promise<void> {
    const response: any = await this.http.get(bmxurl + listUrl).toPromise().catch(() => null);
    const items: any[] = (Array.isArray(response) ? response : response?.data) ?? [];

    if (items.some(item => (nameOf(item) ?? '').toString().trim().toLowerCase() == name.toLowerCase())) {
      throw new Error('There is already ' + what + ' called "' + name + '" - choose another name.');
    }
  }

  //#endregion

  //#region Packages' contents

  //Extras, upgrades and admissions on an existing package: each added from the venue's own list,
  //changed, or taken off.
  async proposeExtras(input: any): Promise<packageproposal> {
    return this.proposeContents(input, 'Change package extras', 'pi-shopping-bag', (pkg, lists) => this.editExtras(pkg, input, lists));
  }

  async proposeUpgrades(input: any): Promise<packageproposal> {
    return this.proposeContents(input, 'Change package upgrades', 'pi-star', (pkg, lists) => this.editUpgrades(pkg, input, lists));
  }

  async proposeAdmissions(input: any): Promise<packageproposal> {
    return this.proposeContents(input, 'Change package admissions', 'pi-users', (pkg, lists) => this.editAdmissions(pkg, input, lists));
  }

  //The change is worked out on a copy now, to check it and describe it, and again on a fresh read
  //at Confirm - so anything changed in between is built on rather than overwritten, and a change
  //that no longer fits is refused.
  private async proposeContents(input: any, title: string, icon: string, edit: (pkg: any, lists: any) => string[]): Promise<packageproposal> {
    await this.requireAccess();

    const pkg = await this.fullPackage(input.package);
    const lines = edit(JSON.parse(JSON.stringify(pkg)), await this.venueLists());

    if (lines.length == 0) {
      throw new Error('No changes given.');
    }

    return {
      title: title,
      icon: icon,
      workingText: 'Saving the package...',
      summary: pkg.name + ':\n' + lines.join('\n'),
      apply: async () => {
        await this.requireAccess();

        const fresh = await this.fullPackage(pkg.packageId);
        edit(fresh, await this.venueLists());
        this.prepareForSave(fresh);

        const response: any = await this.http.put(bmxurl + '/package', fresh).toPromise();

        if (!response?.succeeded) {
          throw new Error(response?.messages?.[0] ?? 'The package could not be saved.');
        }

        return 'Package saved.';
      }
    };
  }

  //The venue's extras, upgrades and admissions that can be put on a package.
  private async venueLists(): Promise<{ extras: any[], upgrades: any[], abUpgrades: any[], admissions: any[] }> {
    const get = (url: string) => this.http.get(bmxurl + url).toPromise().then((response: any) => (response?.data ?? []) as any[]).catch(() => [] as any[]);

    const [extras, upgrades, abUpgrades, admissions] = await Promise.all([
      get('ExtraList/' + this.venueId + '/false'),
      get('UpgradeListall/' + this.venueId + '/false'),
      get('AdmissionBasedUpgradeList/' + this.venueId + '/false'),
      get('AdmissionList/' + this.venueId + '/false')
    ]);

    const live = (item: any) => item != null && item.disabled != true;

    return { extras: extras.filter(live), upgrades: upgrades.filter(live), abUpgrades: abUpgrades.filter(live), admissions: admissions.filter(live) };
  }

  private editExtras(pkg: any, input: any, lists: any): string[] {
    const lines: string[] = [];
    pkg.extras = pkg.extras ?? [];
    const onPackage = () => pkg.extras as any[];
    const range = (extra: any) => extra.variablevalue ? 'staff enter the value' : extra.min + '-' + extra.max + (extra.allparticipants ? ', one per participant' : '');

    (Array.isArray(input.remove) ? input.remove : []).forEach((wanted: any) => {
      const extra = this.pick(onPackage(), wanted, e => e.extraId, e => e.name, 'extra on ' + pkg.name);
      pkg.extras = onPackage().filter(e => e.id != extra.id);
      lines.push('Take off extra ' + extra.name);
    });

    (Array.isArray(input.change) ? input.change : []).forEach((item: any) => {
      const extra = this.pick(onPackage(), item.extra, e => e.extraId, e => e.name, 'extra on ' + pkg.name);
      const was = range(extra);
      this.setExtraNumbers(extra, item);
      lines.push(extra.name + ': ' + was + ' → ' + range(extra));
    });

    (Array.isArray(input.add) ? input.add : []).forEach((item: any) => {
      const product = this.pick(lists.extras, item.extra, (e: any) => e.extraId, (e: any) => e.name, 'extra');

      if (onPackage().some(e => e.extraId == product.extraId)) {
        throw new Error(product.name + ' is already on ' + pkg.name + '.');
      }

      const extra = this.packageExtra(product, item);
      pkg.extras.push(extra);
      lines.push('Add extra ' + extra.name + ' (' + this.extraPricing(extra) + ', ' + range(extra) + ')');
    });

    return lines;
  }

  private editUpgrades(pkg: any, input: any, lists: any): string[] {
    const lines: string[] = [];
    pkg.packageupgrades = pkg.packageupgrades ?? [];
    pkg.packageabupgrades = pkg.packageabupgrades ?? [];

    const onPackage = () => [
      ...(pkg.packageupgrades as any[]).map(u => ({ item: u, ab: false })),
      ...(pkg.packageabupgrades as any[]).map(u => ({ item: u, ab: true }))
    ];
    const nameOf = (u: any) => u?.name ?? u?.upgrade?.name;
    const priceText = (u: any) => (Number(u.price) || 0).toFixed(2) + (u.perperson ? ' per person' : '');

    (Array.isArray(input.remove) ? input.remove : []).forEach((wanted: any) => {
      const found = this.pick(onPackage(), wanted, f => f.item.upgrade?.id, f => nameOf(f.item), 'upgrade on ' + pkg.name);

      if (found.ab) {
        pkg.packageabupgrades = pkg.packageabupgrades.filter((u: any) => u !== found.item);
      }
      else {
        pkg.packageupgrades = pkg.packageupgrades.filter((u: any) => u !== found.item);
      }

      lines.push('Take off upgrade ' + nameOf(found.item));
    });

    (Array.isArray(input.change) ? input.change : []).forEach((item: any) => {
      const found = this.pick(onPackage(), item.upgrade, f => f.item.upgrade?.id, f => nameOf(f.item), 'upgrade on ' + pkg.name);
      const was = priceText(found.item);
      this.setUpgradeNumbers(found.item, item);
      lines.push(nameOf(found.item) + ': ' + was + ' → ' + priceText(found.item));
    });

    (Array.isArray(input.add) ? input.add : []).forEach((item: any) => {
      //Ordinary and admission based upgrades are separate lists; kind settles a name in both.
      const candidates = [
        ...(item.kind == 'admission_based' ? [] : lists.upgrades.map((u: any) => ({ product: u, ab: false }))),
        ...(item.kind == 'upgrade' ? [] : lists.abUpgrades.map((u: any) => ({ product: u, ab: true })))
      ];
      const found = this.pick(candidates, item.upgrade, c => c.product.id, c => c.product.name, 'upgrade');
      const list: any[] = found.ab ? pkg.packageabupgrades : pkg.packageupgrades;

      if (list.some(u => (u.upgrade?.id != null && u.upgrade.id == found.product.id) || nameOf(u) == found.product.name)) {
        throw new Error(found.product.name + ' is already on ' + pkg.name + '.');
      }

      //Package Upgrades allows three of each kind.
      if (list.length >= 3) {
        throw new Error(pkg.name + ' already has 3 ' + (found.ab ? 'admission based upgrades' : 'upgrades') + ', the most a package can have. Take one off first.');
      }

      const upgrade = this.packageUpgrade(found.product, item, found.ab);
      list.push(upgrade);
      lines.push('Add ' + (found.ab ? 'admission based upgrade ' : 'upgrade ') + upgrade.name + ' at ' + priceText(upgrade));
    });

    //Package Upgrades saves its upgrades in the order shown.
    (pkg.packageupgrades as any[]).forEach((u, index) => u.order = index);

    return lines;
  }

  private editAdmissions(pkg: any, input: any, lists: any): string[] {
    const lines: string[] = [];
    pkg.admissions = pkg.admissions ?? [];
    const onPackage = () => pkg.admissions as any[];
    const describe = (adm: any) => (Number(adm.price) || 0).toFixed(2) + (adm.fixedPrice ? ' per booking' : ' each') + ', ' + adm.min + '-' + adm.max;

    (Array.isArray(input.remove) ? input.remove : []).forEach((wanted: any) => {
      const adm = this.pick(onPackage(), wanted, a => a.admissionid, a => a.name, 'admission on ' + pkg.name);
      pkg.admissions = onPackage().filter(a => a.id != adm.id);
      lines.push('Take off admission ' + adm.name);
    });

    (Array.isArray(input.change) ? input.change : []).forEach((item: any) => {
      const adm = this.pick(onPackage(), item.admission, a => a.admissionid, a => a.name, 'admission on ' + pkg.name);
      const was = describe(adm);
      this.setAdmissionNumbers(adm, item);
      lines.push(adm.name + ': ' + was + ' → ' + describe(adm));
    });

    (Array.isArray(input.add) ? input.add : []).forEach((item: any) => {
      const product = this.pick(lists.admissions, item.admission, (a: any) => a.admissionid, (a: any) => a.name, 'admission');

      if (onPackage().some(a => a.admissionid == product.admissionid)) {
        throw new Error(product.name + ' is already on ' + pkg.name + '.');
      }

      const adm = this.packageAdmission(product);
      this.setAdmissionNumbers(adm, item);
      pkg.admissions.push(adm);
      lines.push('Add admission ' + adm.name + ' (' + describe(adm) + ')');
    });

    if (onPackage().length == 0) {
      throw new Error('A package needs at least one admission.');
    }

    onPackage().forEach(adm => {
      if (Number(adm.max) < Number(adm.min)) {
        throw new Error('The most of ' + adm.name + ' cannot be less than the least.');
      }
    });

    //Package Edit's rules on admissions.
    if ((pkg.resources ?? []).some((res: any) => res.useClasses == true) && onPackage().some(adm => adm.admissionClass == null)) {
      throw new Error('One of the package\'s resources uses classes, so every admission needs a class - set that up on Package Edit.');
    }

    if (onPackage().some(adm => adm.countstowardsmin == false && (adm.message ?? '') == '')) {
      throw new Error('An admission that does not count towards the minimum needs a message - set that up on Package Edit.');
    }

    return lines;
  }

  async proposeAddToGroup(input: any): Promise<packageproposal> {
    await this.requireAccess();

    const pkg = await this.fullPackage(input.package);
    const group = await this.group(input.group);

    if ((group.packages ?? []).some((member: any) => member.packageId == pkg.packageId)) {
      throw new Error(pkg.name + ' is already in ' + group.packagegroupname + '.');
    }

    return {
      title: 'Add to package group',
      icon: 'pi-th-large',
      workingText: 'Saving the group...',
      summary: 'Add ' + pkg.name + ' to the package group "' + group.packagegroupname + '"?',
      apply: async () => {
        await this.requireAccess();
        await this.addToGroup(pkg.packageId, group.packagegroupid);
        return 'Added to ' + group.packagegroupname + '.';
      }
    };
  }

  //Date ranges added to or taken off a time set. A change that would make two time sets cover
  //the same date is refused - time sets must never overlap.
  async proposeTimeSetDates(input: any): Promise<packageproposal> {
    await this.requireAccess();

    const sets = await this.timeSets();
    const set = this.pick(sets, input.time_set, item => item.id, item => item.name, 'time set');

    if (set.isdefault) {
      throw new Error('"' + set.name + '" is the default time set: it covers every date no other time set does, so it has no dates of its own.');
    }

    const adding = this.rangesFrom(input.add);
    const removing = this.rangesFrom(input.remove);

    if (adding.length == 0 && removing.length == 0) {
      throw new Error('No date ranges given to add or remove.');
    }

    const after = this.changedRanges(set, adding, removing);
    const clashes = this.overlaps(sets.map(item => item.id == set.id ? { ...item, holidayopenings: after } : item))
      .filter(item => item.aId == set.id || item.bId == set.id);

    if (clashes.length > 0) {
      throw new Error('That would make time sets overlap: ' + clashes.map(item => item.a + ' and ' + item.b + ' would both cover ' +
        this.dayText(item.from) + ' to ' + this.dayText(item.to)).join('; ') + '. Time sets must never overlap.');
    }

    const lines = [
      ...removing.map(range => 'Remove ' + this.dayText(range.from) + ' to ' + this.dayText(range.to)),
      ...adding.map(range => 'Add ' + this.dayText(range.from) + ' to ' + this.dayText(range.to))
    ];

    return {
      title: 'Change time set dates',
      icon: 'pi-calendar',
      workingText: 'Saving the time set...',
      summary: 'Time set "' + set.name + '":\n' + lines.join('\n'),
      apply: async () => {
        await this.requireAccess();

        //Read again and checked again, in case either time set changed while the card waited.
        const freshSets = await this.timeSets();
        const fresh: any = await this.http.get(bmxurl + 'timesets/' + this.venueId + '/' + set.id).toPromise();

        if (!fresh?.succeeded || fresh.data == null) {
          throw new Error('The time set could not be read.');
        }

        const target = fresh.data;
        target.holidayopenings = this.changedRanges(target, adding, removing);

        const stillClashes = this.overlaps(freshSets.map(item => item.id == target.id ? target : item))
          .filter(item => item.aId == target.id || item.bId == target.id);

        if (stillClashes.length > 0) {
          throw new Error('Another time set now covers some of those dates, so nothing was changed.');
        }

        target.disabled = false;
        const response: any = await this.http.put(bmxurl + '/timesets', target).toPromise();

        if (!response?.succeeded) {
          throw new Error(response?.messages?.[0] ?? 'The time set could not be saved.');
        }

        return 'Time set saved.';
      }
    };
  }

  //A new package: a copy of an existing one with changes, or built from the venue's own
  //resources and admissions. Checked here against the rules Package Edit applies before a save.
  async proposeCreate(input: any): Promise<packageproposal> {
    await this.requireAccess();

    const name = (input.name ?? '').toString().trim();

    if (name == '') {
      throw new Error('The new package needs a name.');
    }

    const existing = await this.packageList();

    if (existing.some(item => (item.name ?? '').toString().trim().toLowerCase() == name.toLowerCase())) {
      throw new Error('There is already a package called "' + name + '" - choose another name.');
    }

    const copyFrom = input.copy_from != null && input.copy_from !== '' ? await this.fullPackage(input.copy_from) : null;
    const pkg = copyFrom != null ? this.copyOf(copyFrom, name) : await this.newPackage(name, input);
    const lines: string[] = [copyFrom != null ? 'A copy of ' + copyFrom.name + ', called ' + name : 'New package ' + name];

    if (input.description != null && input.description !== '') {
      pkg.description = input.description.toString();
    }

    if ((pkg.description ?? '').toString().trim() == '') {
      throw new Error('The new package needs a description.');
    }

    if (input.visibility != null) {
      const value = VISIBILITY[(input.visibility ?? '').toString().toLowerCase()];

      if (value == null) {
        throw new Error('visibility must be one of hidden, public, venue or both.');
      }

      pkg.visibility = value;
    }

    if (input.start_date != null) {
      pkg.startDate = this.wallClockDay(this.dayOrFail(input.start_date, 'start_date'));
    }

    if (input.end_date != null) {
      pkg.endDate = this.wallClockDay(this.dayOrFail(input.end_date, 'end_date'));
    }

    if (input.min_group != null) {
      pkg.min = Number(input.min_group) || 0;
    }

    if (input.max_group != null) {
      pkg.max = Number(input.max_group) || 0;
    }

    //On a copy, admissions only have their price and numbers changed.
    if (copyFrom != null && Array.isArray(input.admissions)) {
      input.admissions.forEach((item: any) => {
        const adm = this.pick<any>(pkg.admissions ?? [], item.admission, a => a.admissionid, a => a.name, 'admission on ' + copyFrom.name);
        this.setAdmissionNumbers(adm, item);
      });
    }

    if (input.is_party_package != null) {
      pkg.ispartypackage = input.is_party_package == true;
    }

    //Package Edit asks about a package that mentions a party but is not marked as one.
    if (/party/i.test(pkg.name + ' ' + pkg.description) && pkg.ispartypackage != true && input.is_party_package == null) {
      throw new Error('The name or description mentions a party. Ask staff whether this is a party package, and give is_party_package.');
    }

    //Extras and upgrades from the venue's lists - on a copy, added to those it already has.
    const contentLines: string[] = [];

    if (Array.isArray(input.extras) || Array.isArray(input.upgrades)) {
      const lists = await this.venueLists();
      contentLines.push(...this.editExtras(pkg, { add: input.extras }, lists));
      contentLines.push(...this.editUpgrades(pkg, { add: input.upgrades }, lists));
    }

    this.checkLikePackageEdit(pkg);

    lines.push(this.describeForCard(pkg));
    lines.push(...contentLines);

    //Start times: copied from the package being copied, unless told not to, with any given on top.
    const sets = await this.timeSets();
    const startTimes: { set: any, days: any }[] = [];

    if (copyFrom != null && input.copy_start_times !== false) {
      for (const set of sets) {
        const record = await this.startTimesFor(copyFrom.packageId, set.id);

        if (record != null) {
          startTimes.push({ set: set, days: this.allDays(record) });
        }
      }
    }

    for (const item of (Array.isArray(input.start_times) ? input.start_times : [])) {
      const set = this.pick(sets, item.time_set, s => s.id, s => s.name, 'time set');
      const days = this.daysFrom(item.days);
      const entry = startTimes.find(st => st.set.id == set.id);

      if (entry != null) {
        Object.assign(entry.days, days);
      }
      else {
        startTimes.push({ set: set, days: days });
      }
    }

    lines.push(startTimes.length == 0
      ? 'Start times: none - the package will not show on any day until they are set'
      : 'Start times: ' + startTimes.map(st => '"' + st.set.name + '" ' + this.timesSummary(st.days)).join('; '));

    const group = input.package_group != null && input.package_group !== '' ? await this.group(input.package_group) : null;
    lines.push(group != null ? 'Package group: ' + group.packagegroupname : 'Package group: none - Make a Booking will not offer it until it is in one');

    return {
      title: copyFrom != null ? 'Copy package' : 'Create package',
      icon: 'pi-plus-circle',
      workingText: 'Creating the package...',
      summary: lines.join('\n'),
      apply: async () => {
        await this.requireAccess();

        if ((await this.packageList()).some(item => (item.name ?? '').toString().trim().toLowerCase() == name.toLowerCase())) {
          throw new Error('A package called "' + name + '" has been made since, so nothing was created.');
        }

        this.prepareForSave(pkg);
        const response: any = await this.http.post(bmxurl + '/package', pkg).toPromise();

        if (!response?.succeeded || response.data?.packageId == null) {
          throw new Error(response?.messages?.[0] ?? 'The package could not be created.');
        }

        const packageId = response.data.packageId;
        const problems: string[] = [];

        //The package exists from here on, so what follows only reports a failure.
        for (const st of startTimes) {
          await this.saveStartTimes(packageId, st.set, st.days).catch(error => problems.push('start times for "' + st.set.name + '" (' + (error?.message ?? 'failed') + ')'));
        }

        if (group != null) {
          await this.addToGroup(packageId, group.packagegroupid).catch(error => problems.push('adding it to ' + group.packagegroupname + ' (' + (error?.message ?? 'failed') + ')'));
        }

        return 'Package "' + name + '" created.' + (problems.length > 0 ? ' These did not save: ' + problems.join('; ') +
          '. The assistant can try them again (copy_start_times or set_start_times, add_package_to_group).' : '');
      }
    };
  }

  //#endregion

  //#region Saving

  private async saveStartTimes(packageId: string, set: any, days: { [weekday: string]: any[] }): Promise<void> {
    const existing = await this.startTimesFor(packageId, set.id);
    const timesFor = (weekday: string) => days[weekday] ?? [...(existing?.[weekday + 'times'] ?? [])];

    const payload: any = {
      id: existing?.id ?? null,
      name: set.name,
      venueid: this.venueId,
      packageid: packageId,
      timesetid: set.id,
      isdefault: set.isdefault,
      disabled: false,
      mondaytimes: timesFor('monday'),
      tuesdaytimes: timesFor('tuesday'),
      wednesdaytimes: timesFor('wednesday'),
      thursdaytimes: timesFor('thursday'),
      fridaytimes: timesFor('friday'),
      saturdaytimes: timesFor('saturday'),
      sundaytimes: timesFor('sunday')
    };

    const saved: any = await (payload.id != null
      ? this.http.put(bmxurl + '/packagestarttimes', payload)
      : this.http.post(bmxurl + '/packagestarttimes', payload)).toPromise();

    if (!saved?.succeeded) {
      throw new Error(saved?.messages?.[0] ?? 'The start times could not be saved.');
    }
  }

  private async addToGroup(packageId: string, groupId: string): Promise<void> {
    const response: any = await this.http.get(bmxurl + '/PackageGroup/' + this.venueId + '/' + groupId).toPromise();

    if (!response?.succeeded || response.data == null) {
      throw new Error('The package group could not be read.');
    }

    const group = response.data;
    group.packages = group.packages ?? [];

    if (group.packages.some((member: any) => member.packageId == packageId)) {
      return;
    }

    //The package as the package list gives it, set out the way Package Group Edit adds one.
    const product = (await this.packageList()).find(item => item.packageId == packageId);

    if (product == null) {
      throw new Error('The package could not be found to add to the group.');
    }

    group.packages.push({ ...product, venueID: this.venueId, packageabupgrades: product.admissionbasedupgrades ?? product.packageabupgrades });
    group.disabled = false;

    const saved: any = await this.http.put(bmxurl + '/packagegroup', group).toPromise();

    if (!saved?.succeeded) {
      throw new Error(saved?.messages?.[0] ?? 'The package group could not be saved.');
    }
  }

  //Package Edit's tidying before a save (updateData and proceedWithSave), apart from the steps
  //that only undo what its own screen does on load.
  private prepareForSave(pkg: any): void {
    pkg.name = (pkg.name ?? '').toString().trim();

    (pkg.resources ?? []).forEach((res: any, index: number) => {
      res.selectableresourcestrings = selectableIdsToSave(res);

      res.orderindex = index;
      (res.resources ?? []).forEach((inner: any, innerIndex: number) => inner.orderindex = innerIndex);
    });

    (pkg.admissions ?? []).forEach((adm: any, index: number) => adm.index = index);
  }

  //Package Edit's checks before a save.
  private checkLikePackageEdit(pkg: any): void {
    const resources: any[] = pkg.resources ?? [];
    const admissions: any[] = pkg.admissions ?? [];

    if (admissions.length == 0) {
      throw new Error('A package needs at least one admission.');
    }

    if (resources.length == 0) {
      throw new Error('A package needs at least one resource.');
    }

    if (resources[0].mode == 'COMPLEX') {
      throw new Error('The first resource cannot be a group of resources.');
    }

    if (resources.some(res => !(Number(res.length) > 0))) {
      throw new Error('Every resource needs a length of more than 0 minutes.');
    }

    if (resources.some(res => res.useClasses == true) && admissions.some(adm => adm.admissionClass == null)) {
      throw new Error('One of the resources uses classes, so every admission needs a class - set that up on Package Edit.');
    }

    if (admissions.some(adm => adm.countstowardsmin == false && (adm.message ?? '') == '')) {
      throw new Error('An admission that does not count towards the minimum needs a message - set that up on Package Edit.');
    }

    if (pkg.publicDepositPolicy == null || pkg.venueDepositPolicy == null) {
      throw new Error('A package needs both a public and a venue deposit policy.');
    }

    if (Number(pkg.max) < Number(pkg.min)) {
      throw new Error('The maximum group size cannot be less than the minimum.');
    }

    const from = this.keyOfText(pkg.startDate);
    const to = this.keyOfText(pkg.endDate);

    if (from != null && to != null && to < from) {
      throw new Error('The end date cannot be before the start date.');
    }
  }

  //Copy Package's copy: the package posted again as a new one, remembering where it came from.
  private copyOf(source: any, name: string): any {
    const copy = JSON.parse(JSON.stringify(source));
    copy.id = null;
    copy.packageId = null;
    copy.name = name;
    copy.originatingid = source.packageId;
    copy.disabled = false;
    return copy;
  }

  //A package built from the venue's lists, starting from Package Edit's new-package defaults.
  //Each resource runs one after the other, as Package Edit adds a plain resource.
  private async newPackage(name: string, input: any): Promise<any> {
    const get = (url: string) => this.http.get(bmxurl + url).toPromise().then((response: any) => response?.data ?? []);

    const [resources, admissions, deposits, confirmations, questionnaires, fees] = await Promise.all([
      get('ResourceListForPackage/' + this.venueId + '/false'),
      get('AdmissionList/' + this.venueId + '/false'),
      get('DepositPolicyList/' + this.venueId + '/false'),
      get('ConfirmationList/' + this.venueId).catch(() => []),
      get('QuestionnaireList/' + this.venueId).catch(() => []),
      get('BookingFeeList/' + this.venueId + '/false').catch(() => [])
    ]);

    const usable = (resources as any[]).filter(res => (res.eventid ?? '') == '' && res.disabled != true);

    if (!Array.isArray(input.resources) || input.resources.length == 0) {
      throw new Error('A new package needs its resources, in the order they are used, each with a length in minutes. package_building_blocks lists them.');
    }

    if (!Array.isArray(input.admissions) || input.admissions.length == 0) {
      throw new Error('A new package needs its admissions. package_building_blocks lists them.');
    }

    const packageResources = input.resources.map((item: any) => {
      const res = this.pick(usable, item.resource, r => r.resourceId, r => r.name, 'resource');

      if (res.isresourcecontainer) {
        throw new Error(res.name + ' is a group of resources, which the assistant can only use by copying a package that has it. Copy such a package, or add it on Package Edit.');
      }

      return {
        groupname: '', id: uuidv4(), resourceId: res.resourceId, name: res.name, useClasses: res.useClasses, exclusive: res.exclusive,
        eventid: res.eventid, concurrentUses: res.concurrentUses, maxConcurrent: res.maxConcurrent, disabled: res.disabled,
        venueId: res.venueId, colour: res.colour, orderindex: res.orderindex,
        length: item.length_minutes != null ? Number(item.length_minutes) : res.length,
        classes: res.classes, showonbooking: res.showonbooking, resources: res.resources, isresourcecontainer: res.isresourcecontainer,
        isconstructed: res.isconstructed, price: res.price, fixedprice: res.fixedprice, resourcegroupname: res.resourcegroupname,
        tags: res.tags, resourcegroup: res.resourcegroup, selectionmode: '', selectableresourcestrings: res.selectableresourcestrings,
        startwhen: '', startTimeOffset: res.startTimeOffset, optional: res.optional, quantityselector: 'GROUPSIZE',
        quantity: res.quantity, mode: 'SIMPLE', inline: true, ignore: false, admissions: null, inProgress: true, originatingid: '',
        wasoptional: false, wasselected: false, isupgrade: false, packageupgradeid: null, showinitinerary: true, showondaysheet: true,
        cansplit: res.cansplit
      };
    });

    const usableAdmissions = (admissions as any[]).filter(adm => adm.disabled != true);
    const packageAdmissions = input.admissions.map((item: any) => {
      const adm = this.pick(usableAdmissions, item.admission, a => a.admissionid, a => a.name, 'admission');
      const added = this.packageAdmission(adm);

      this.setAdmissionNumbers(added, item);
      return added;
    });

    if (packageAdmissions.some((adm: any, index: number) => packageAdmissions.findIndex((other: any) => other.admissionid == adm.admissionid) != index)) {
      throw new Error('The same admission is given twice.');
    }

    const deposit = (wanted: any, which: string) => {
      if (wanted == null || wanted === '') {
        throw new Error('A new package needs a ' + which + ' deposit policy. package_building_blocks lists them.');
      }

      return this.pick((deposits as any[]).filter(dp => dp.disabled != true), wanted, dp => dp.id, dp => dp.name, 'deposit policy');
    };

    const defaultFee = (fees as any[]).find(fee => fee.isdefault === true);
    let bookingFeeId = null;

    //Package Edit offers the venue's default booking fee on a new package.
    if (defaultFee != null) {
      if (input.booking_fee == null) {
        throw new Error('The venue has a default booking fee, "' + defaultFee.name + '". Ask staff whether the new package should charge it, and give booking_fee as "default" or "none".');
      }

      bookingFeeId = input.booking_fee == 'default' ? defaultFee.bfid : null;
    }

    const today = new Date();
    const fiveYears = new Date(today.getFullYear() + 5, today.getMonth(), today.getDate());

    return {
      id: null, packageId: null, name: name, eventid: null, description: '', imageurl: '', message: '', disabled: false,
      venueID: this.venueId, resources: packageResources, startTimes: null, admissions: packageAdmissions, packageLength: 0,
      questionnaire: input.questionnaire != null && input.questionnaire !== ''
        ? this.pick(questionnaires as any[], input.questionnaire, q => q.questionnaireid ?? q.id, q => q.name, 'questionnaire') : null,
      visibility: 'VENUE', min: 0, max: 0, tags: null,
      startDate: this.wallClockDay(today), endDate: this.wallClockDay(fiveYears),
      colour: '#990000', packageRule: null, packageRestriction: null, extras: null,
      publicDepositPolicy: deposit(input.public_deposit_policy, 'public'),
      venueDepositPolicy: deposit(input.venue_deposit_policy, 'venue'),
      additionalresources: null,
      confirmation: input.confirmation != null && input.confirmation !== ''
        ? this.pick((confirmations as any[]).filter(c => c.disabled != true), input.confirmation, c => c.confirmationid ?? c.id, c => c.name, 'confirmation') : null,
      warning: null, originatingid: '', bookingscannotbemadewithinxminutes: 0, bookingscannotbemadeafterxminutes: 0,
      cannotmakebookingstoday: true, discounttype: 'PERCENTAGE', discountamount: 0, discountcode: null,
      discountstart: this.wallClockDay(today), discountend: this.wallClockDay(fiveYears), earlybirddiscount: false,
      discountrangetype: 0, discountdaysbefore: 0, imageguid: uuidv4(), bookingtimeout: 300, maxcalendarmonths: 3,
      packageupgrades: [], packageabupgrades: [], waiverrequired: false, ispartypackage: false, bookingfeeid: bookingFeeId,
      portaltemplateid: null, cannotbepaidwithvoucher: false
    };
  }

  //The package's own copy of one of the venue's admissions, as Package Edit's AddToAdmissions makes it.
  private packageAdmission(adm: any): any {
    return {
      id: uuidv4(), name: adm.name, classid: null, bookingwizardtext: adm.bookingwizardtext, fixedPrice: adm.fixedPrice,
      participant: adm.participant, venueid: adm.venueid, admissionid: adm.admissionid, noofpeople: adm.noofpeople,
      price: adm.price, admissionClass: adm.admissionClass, min: adm.min, max: adm.max, index: adm.index, disabled: adm.disabled,
      mustinclude: adm.mustinclude, eventtickettype: adm.eventtickettype, counter: adm.counters, linkedadmissions: adm.linkedadmissions,
      uselinkedadmissions: adm.uselinkedadmissions, linkeddiscounts: adm.linkeddiscounts, uselinkeddiscounts: adm.uselinkeddiscounts,
      maxlinkeddiscounts: adm.maxlinkeddiscounts, maxlinkedadmissions: adm.maxlinkedadmissions, countstowardsmin: adm.countstowardsmin,
      countstowardsmax: adm.countstowardsmax, message: adm.message, fixed: adm.fixed, onlysetmax: adm.onlysetmax
    };
  }

  //The package's own copy of one of the venue's extras, as Package Extras' AddToList makes it. A
  //variable value extra (staff type its value on the booking) is fixed at 0 to 1, as that screen fixes it.
  private packageExtra(product: any, item: any): any {
    const extra: any = {
      id: uuidv4(), packageExtraId: uuidv4(), extraId: product.extraId, name: product.name, description: product.description,
      venueId: product.venueId, monday: product.monday, tuesday: product.tuesday, wednesday: product.wednesday,
      thursday: product.thursday, friday: product.friday, saturday: product.saturday, sunday: product.sunday,
      extraOptions: null, price: product.price, perPerson: product.perPerson, min: product.min,
      max: product.max <= 0 ? 1 : product.max, qty: product.qty, question: product.question, answer: product.answer,
      allparticipants: item?.all_participants == true, selected: false, venueextras: product.venueextras,
      disabled: product.disabled, imageurl: product.imageurl, singledate: product.singledate, variablevalue: product.variablevalue == true
    };

    this.setExtraNumbers(extra, item);
    return extra;
  }

  private setExtraNumbers(extra: any, item: any): void {
    if (item?.min != null) {
      extra.min = Math.max(Number(item.min) || 0, 0);
    }

    if (item?.max != null) {
      extra.max = Math.max(Number(item.max) || 0, 0);
    }

    if (item?.all_participants != null) {
      extra.allparticipants = item.all_participants == true;
    }

    if (extra.variablevalue == true) {
      extra.min = 0;
      extra.max = 1;
    }

    if (extra.max < extra.min) {
      throw new Error('The most of ' + extra.name + ' cannot be less than the least.');
    }
  }

  //The package's own copy of one of the venue's upgrades, as Package Upgrades' AddToList and
  //AddToABList make it. Its price on the package starts at 0 there; here it is given.
  private packageUpgrade(product: any, item: any, admissionBased: boolean): any {
    const upgrade: any = {
      id: uuidv4(), name: product.name, packageupgradeid: uuidv4(), disabled: false, upgrade: product, price: 0,
      venueid: product.venueId, perperson: false, order: 0, isupgrade: true, quantityremaining: 0, optional: false
    };

    if (admissionBased) {
      upgrade.quantity = 0;
    }

    this.setUpgradeNumbers(upgrade, item);
    return upgrade;
  }

  private setUpgradeNumbers(upgrade: any, item: any): void {
    if (item?.price != null) {
      const price = Number(item.price);

      if (isNaN(price) || price < 0) {
        throw new Error('The price for ' + upgrade.name + ' must be a number, 0 or more.');
      }

      upgrade.price = price;
    }

    if (item?.per_person != null) {
      upgrade.perperson = item.per_person == true;
    }
  }

  private setAdmissionNumbers(adm: any, item: any): void {
    if (item.price != null) {
      const price = Number(item.price);

      if (isNaN(price) || price < 0) {
        throw new Error('The price for ' + adm.name + ' must be a number, 0 or more.');
      }

      adm.price = price;
    }

    if (item.min != null) {
      adm.min = Number(item.min) || 0;
    }

    if (item.max != null) {
      adm.max = Number(item.max) || 0;
    }
  }

  //#endregion

  //#region Looking things up

  //Staff can only have the assistant change packages and time sets if they could do it on the
  //Packages screens themselves.
  private async requireAccess(code: string = 'PACKAGE', screen: string = 'Packages', what: string = 'packages or time sets'): Promise<void> {
    const response: any = await this.http.get(bmxurl + 'User/' + (localStorage.getItem('SmartUserID') ?? '')).toPromise();

    if (!response?.succeeded || !getAccess(code, response.data)) {
      throw new Error('You do not have access to ' + screen + ', so the assistant cannot change ' + what + ' for you.');
    }
  }

  //How an extra is priced, in the Extras screen's words: perPerson true is "Per Booking", false
  //"Per Extra" (Multi Package Extras says the same).
  private extraPricing(extra: any): string {
    return extra?.variablevalue ? 'value entered by staff' : (Number(extra?.price) || 0).toFixed(2) + (extra?.perPerson ? ' per booking' : ' per extra');
  }

  //Every package, including switched off ones, but not the ones behind events.
  private async packageList(): Promise<any[]> {
    const response: any = await this.http.get(bmxurl + 'packagelist/' + this.venueId + '/true').toPromise();
    return ((Array.isArray(response) ? response : response?.data) ?? []).filter((item: any) => item.eventid == null);
  }

  private async fullPackage(wanted: any): Promise<any> {
    const listed = this.pick(await this.packageList(), wanted, item => item.packageId, item => item.name, 'package');
    const response: any = await this.http.get(bmxurl + 'package/' + this.venueId + '/' + listed.packageId).toPromise();

    if (!response?.succeeded || response.data == null) {
      throw new Error('The package ' + listed.name + ' could not be read.');
    }

    return response.data;
  }

  private async timeSets(): Promise<any[]> {
    const response: any = await this.http.get(bmxurl + 'TimesetList/' + this.venueId + '/false').toPromise();
    return (response?.succeeded ? response.data : null) ?? [];
  }

  private async timeSet(wanted: any): Promise<any> {
    return this.pick(await this.timeSets(), wanted, item => item.id, item => item.name, 'time set');
  }

  //A package's start time record for a time set, or null when none has been saved. For a package
  //with no record the API can answer 400 Bad Request rather than an empty answer (seen on a
  //package just created), so that is read as "none saved" too - as the Start Times screen, which
  //then saves a new record, effectively does.
  private async startTimesFor(packageId: string, timeSetId: string): Promise<any> {
    try {
      const response: any = await this.http.get(bmxurl + 'PackageStartTime/' + this.venueId + '/' + packageId + '/' + timeSetId).toPromise();
      return response?.data ?? null;
    }
    catch (error: any) {
      if (error?.status == 400 || error?.status == 404) {
        return null;
      }

      throw error;
    }
  }

  private async venue(): Promise<any> {
    const response: any = await this.http.get(bmxurl + 'venue/' + this.venueId).toPromise().catch(() => null);
    return response?.data ?? null;
  }

  //The groups, each read in full, since only a group read on its own lists its packages.
  private async groups(): Promise<any[]> {
    const response: any = await this.http.get(bmxurl + 'PackageGroupListVenue/' + this.venueId + '/false').toPromise();
    const listed: any[] = response?.data ?? [];

    return Promise.all(listed.map(group => this.http.get(bmxurl + '/PackageGroup/' + this.venueId + '/' + group.packagegroupid).toPromise()
      .then((full: any) => full?.data ?? group)
      .catch(() => group)));
  }

  private async groupsWith(packageId: string): Promise<any[]> {
    return (await this.groups()).filter(group => (group.packages ?? []).some((member: any) => member.packageId == packageId));
  }

  private async group(wanted: any): Promise<any> {
    return this.pick(await this.groups(), wanted, group => group.packagegroupid, group => group.packagegroupname, 'package group');
  }

  //Whether Make a Booking's package list for the day - the API's own - has the package.
  private async listedOn(packageId: string, day: Date): Promise<boolean> {
    const response: any = await this.http.get(bmxurl + 'PackageGroupList/' + this.venueId + '/false/' + this.ddMMyy(day)).toPromise().catch(() => null);

    if (response == null) {
      return null;
    }

    return ((response?.data ?? []) as any[]).some(group => (group.packages ?? []).some((member: any) => member.packageId == packageId));
  }

  private async venueClosingsOn(day: Date): Promise<any[]> {
    const response: any = await this.http.get(bmxurl + '/VenueClosingList/' + this.venueId + '/false/' + this.ddMMyy(day)).toPromise().catch(() => null);
    return ((response?.data ?? []) as any[]).filter(closing => closing != null && closing.disabled != true);
  }

  //Closings and capacity changes on the package's resources for the day, read the way the
  //Daysheet reads them (windowsForDay there).
  private async resourceWindowsOn(pkg: any, day: Date): Promise<{ severity: string, finding: string }[]> {
    const ids = new Set<string>();

    (pkg.resources ?? []).forEach((res: any) => {
      ids.add((res.resourceId ?? '').toString());
      (res.resources ?? []).forEach((inner: any) => ids.add((inner.resourceId ?? '').toString()));
    });

    const [closings, reductions] = await Promise.all([
      this.http.get(bmxurl + 'ResourceClosingList/' + this.venueId + '//false').toPromise().then((r: any) => r?.data ?? []).catch(() => []),
      this.http.get(bmxurl + 'ResourceReductionList/' + this.venueId + '/false').toPromise().then((r: any) => r?.data ?? []).catch(() => [])
    ]);

    const results: { severity: string, finding: string }[] = [];
    const key = this.keyOfDay(day);
    const weekday = WEEKDAYS[day.getDay()];

    const scan = (items: any[], basedOnDay: string, resourceOf: (x: any) => string, describe: (x: any, hours: string) => string) => {
      items.forEach(item => {
        if (item == null || item.disabled || !item.startdate || !item.enddate || !ids.has((resourceOf(item) ?? '').toString())) {
          return;
        }

        const startKey = this.keyOfText(item.startdate);
        const endKey = this.keyOfText(item.enddate);

        if (startKey == null || endKey == null || key < startKey || key > endKey || (item[basedOnDay] && !item[weekday])) {
          return;
        }

        const from = item.startdate.toString().substring(11, 16);
        const to = item.enddate.toString().substring(11, 16);
        const hours = item[basedOnDay] ? (from == to ? 'all day' : from + ' to ' + to) :
          (startKey < key && endKey > key) || (startKey == endKey && to <= from) ? 'all day' :
          (key == startKey ? 'from ' + from : '') + (key == startKey && key == endKey ? ' ' : '') + (key == endKey ? 'until ' + to : '');

        results.push({ severity: 'warning', finding: describe(item, hours) });
      });
    };

    scan(closings, 'closingbasedonday', c => c.resourceid ?? c.resourcetoclose?.resourceId,
      (c, hours) => 'Resource closing "' + c.name + '" closes ' + (c.resourcetoclose?.name ?? 'one of the package\'s resources') + ' ' + hours + '.');
    scan(reductions, 'reductionbasedonday', r => r.resourceid ?? r.resourcetoreduce?.resourceId,
      (r, hours) => 'Capacity change "' + r.name + '" on ' + (r.resourcetoreduce?.name ?? 'one of the package\'s resources') + ' ' + hours + ': ' +
        (r.adjustmenttype == 'NEWCAPACITY' ? 'capacity set to ' + r.adjustmentamount : 'capacity reduced by ' + r.adjustmentamount) + '.');

    return results;
  }

  //Availability in diagnostic mode, as Make a Booking asks it, for a group of the given size
  //(all on the first admission that can be set directly). One line per time.
  private async availabilityOn(pkg: any, day: Date, people: number): Promise<any[]> {
    const admissions = (pkg.admissions ?? []).map((adm: any) => ({ ...adm, noofpeople: 0 }));
    const first = admissions.find((adm: any) => adm.fixed != true) ?? admissions[0];

    if (first == null) {
      return [];
    }

    first.noofpeople = people;

    const req = this.moveBookingService.availabilityRequest(this.venueId, pkg.packageId, admissions, pkg.resources, null, day);
    req.DiagnosticMode = true;
    req.IgnoreExistingBooking = false;

    const response: any = await this.http.post(bmxurl + '/Availability', req).toPromise();

    if (!response?.succeeded) {
      throw new Error('Availability could not be checked.');
    }

    const seen = new Set<string>();

    return ((response.data ?? []) as any[])
      .filter(slot => slot.primaryResource)
      .filter(slot => {
        if (seen.has(slot.time)) return false;
        seen.add(slot.time);
        return true;
      })
      .map(slot => this.compact({
        time: (slot.time ?? '').toString().substring(11, 16),
        available: slot.timeAvailable == true,
        places_left: slot.quantityRemaining,
        reason: (slot.reason ?? '').toString().trim()
      }));
  }

  //The rules on how far ahead and how soon a package can be booked, as they bear on the day.
  private windowFindings(pkg: any, day: Date): { severity: string, finding: string, fix?: string }[] {
    const results: { severity: string, finding: string, fix?: string }[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const daysAway = Math.round((day.getTime() - today.getTime()) / 86400000);

    if (daysAway < 0) {
      results.push({ severity: 'blocks', finding: 'The date is in the past.' });
    }

    if (daysAway == 0 && pkg.cannotmakebookingstoday == true) {
      results.push({ severity: 'blocks', finding: 'The package is set so bookings cannot be made for today.', fix: 'update_package_settings can_book_today' });
    }

    const notice = Number(pkg.bookingscannotbemadewithinxminutes) || 0;

    if (notice > 0 && daysAway >= 0 && daysAway * 1440 < notice + 1440) {
      results.push({ severity: 'warning', finding: 'Bookings need ' + this.minutesText(notice) + ' notice, so some or all times on this date may be too soon.', fix: 'update_package_settings min_notice_minutes' });
    }

    const after = Number(pkg.bookingscannotbemadeafterxminutes) || 0;

    if (after > 0 && daysAway * 1440 > after) {
      results.push({ severity: 'blocks', finding: 'Bookings can only be made up to ' + this.daysAheadText(after) + ' ahead, and this date is ' + daysAway + ' days away.', fix: 'update_package_settings max_days_ahead' });
    }

    const months = Number(pkg.maxcalendarmonths) || 3;
    const limit = new Date(today.getFullYear(), today.getMonth() + months, today.getDate());

    if (day > limit) {
      results.push({ severity: 'warning', finding: 'The booking calendar shows ' + months + ' months ahead, and this date is further out than that.', fix: 'update_package_settings calendar_months' });
    }

    return results;
  }

  //#endregion

  //#region Helpers

  //One item from a list by its id, or by its name: exact first, then the only one containing it.
  private pick<T>(items: T[], wanted: any, idOf: (item: T) => any, nameOf: (item: T) => any, what: string): T {
    const text = (wanted ?? '').toString().trim().toLowerCase();

    if (text == '') {
      throw new Error('No ' + what + ' given.');
    }

    const byId = items.find(item => (idOf(item) ?? '').toString().toLowerCase() == text);
    const exact = items.filter(item => (nameOf(item) ?? '').toString().trim().toLowerCase() == text);
    const partial = items.filter(item => (nameOf(item) ?? '').toString().toLowerCase().includes(text));
    const found = byId ?? (exact.length == 1 ? exact[0] : null) ?? (exact.length == 0 && partial.length == 1 ? partial[0] : null);

    if (found == null) {
      const options = (exact.length > 1 ? exact : partial).map(item => nameOf(item)).slice(0, 15);
      throw new Error(options.length > 1
        ? 'More than one ' + what + ' matches "' + wanted + '": ' + options.join(', ') + '. Which one?'
        : 'No ' + what + ' called "' + wanted + '".');
    }

    return found;
  }

  //Pairs of non-default time sets covering the same dates.
  private overlaps(sets: any[]): { a: string, aId: string, b: string, bId: string, from: string, to: string }[] {
    const results: { a: string, aId: string, b: string, bId: string, from: string, to: string }[] = [];
    const dated = sets.filter(set => !set.isdefault);

    for (let i = 0; i < dated.length; i++) {
      for (let j = i + 1; j < dated.length; j++) {
        for (const one of dated[i].holidayopenings ?? []) {
          for (const two of dated[j].holidayopenings ?? []) {
            const from = this.keyOfText(one.dateFrom) > this.keyOfText(two.dateFrom) ? one.dateFrom : two.dateFrom;
            const to = this.keyOfText(one.dateTo) < this.keyOfText(two.dateTo) ? one.dateTo : two.dateTo;

            if (this.keyOfText(from) <= this.keyOfText(to)) {
              results.push({ a: '"' + dated[i].name + '"', aId: dated[i].id, b: '"' + dated[j].name + '"', bId: dated[j].id, from: from, to: to });
            }
          }
        }
      }
    }

    return results;
  }

  private setCovers(set: any, key: number): boolean {
    return (set.holidayopenings ?? []).some((range: any) => this.keyOfText(range.dateFrom) <= key && key <= this.keyOfText(range.dateTo));
  }

  //A time set's ranges with some taken off (matched on their dates) and some added, in Time Set
  //Edit's form.
  private changedRanges(set: any, adding: { from: Date, to: Date }[], removing: { from: Date, to: Date }[]): any[] {
    let ranges: any[] = [...(set.holidayopenings ?? [])];

    removing.forEach(range => {
      const before = ranges.length;
      ranges = ranges.filter(item => !(this.keyOfText(item.dateFrom) == this.keyOfDay(range.from) && this.keyOfText(item.dateTo) == this.keyOfDay(range.to)));

      if (ranges.length == before) {
        throw new Error('"' + set.name + '" has no range ' + this.dayText(this.wallClockDay(range.from)) + ' to ' + this.dayText(this.wallClockDay(range.to)) + ' to remove.');
      }
    });

    adding.forEach(range => ranges.push({ id: uuidv4(), dateFrom: this.wallClockDay(range.from), dateTo: this.wallClockDay(range.to) }));

    return ranges;
  }

  private rangesFrom(items: any): { from: Date, to: Date }[] {
    return (Array.isArray(items) ? items : []).map((item: any) => {
      const from = this.dayOrFail(item?.from, 'from');
      const to = this.dayOrFail(item?.to, 'to');

      if (to < from) {
        throw new Error('A range ends before it starts.');
      }

      return { from: from, to: to };
    });
  }

  //{ monday: ['10:00', ...] } as start time records hold them: { index: minutes, time: 'HH:mm' },
  //in time order, each once.
  private daysFrom(input: any): { [weekday: string]: any[] } {
    const days: { [weekday: string]: any[] } = {};

    Object.keys(input ?? {}).forEach(name => {
      const weekday = name.toString().trim().toLowerCase();

      if (!WEEKDAYS.includes(weekday)) {
        throw new Error('"' + name + '" is not a day of the week.');
      }

      const times = (Array.isArray(input[name]) ? input[name] : []).map((value: any) => {
        const match = /^(\d{1,2}):(\d{2})$/.exec((value ?? '').toString().trim());

        if (match == null || Number(match[1]) > 23 || Number(match[2]) > 59) {
          throw new Error('"' + value + '" is not a time (HH:mm).');
        }

        const index = Number(match[1]) * 60 + Number(match[2]);
        return { index: index, time: ('0' + match[1]).slice(-2) + ':' + match[2] };
      });

      days[weekday] = times
        .filter((item: any, at: number) => times.findIndex((other: any) => other.index == item.index) == at)
        .sort((one: any, two: any) => one.index - two.index);
    });

    if (Object.keys(days).length == 0) {
      throw new Error('No days given.');
    }

    return days;
  }

  private allDays(record: any): { [weekday: string]: any[] } {
    const days: { [weekday: string]: any[] } = {};
    WEEKDAYS.forEach(weekday => days[weekday] = [...(record?.[weekday + 'times'] ?? [])]);
    return days;
  }

  private timesOn(record: any, weekday: string): string[] {
    return [...(record?.[weekday + 'times'] ?? [])]
      .sort((one: any, two: any) => (Number(one.index) || 0) - (Number(two.index) || 0))
      .map((item: any) => item.time);
  }

  //A start time record as staff read it, Monday first.
  private daysOf(record: any): any {
    const days: any = {};
    [...WEEKDAYS.slice(1), 'sunday'].forEach(weekday => {
      const times = this.timesOn(record, weekday);
      days[this.weekdayName(weekday)] = times.length > 0 ? times.join(', ') : 'none';
    });
    return days;
  }

  private timesSummary(days: { [weekday: string]: any[] }): string {
    return [...WEEKDAYS.slice(1), 'sunday']
      .filter(weekday => days[weekday] != null)
      .map(weekday => this.weekdayName(weekday).substring(0, 3) + ' ' + (days[weekday].length > 0 ? days[weekday].map(item => item.time).join(', ') : 'none'))
      .join(', ');
  }

  private describeForCard(pkg: any): string {
    return [
      'Visibility: ' + this.visibilityText(pkg.visibility),
      'On sale: ' + this.dayText(pkg.startDate) + ' to ' + this.dayText(pkg.endDate),
      'Group size: ' + pkg.min + '-' + pkg.max,
      'Itinerary: ' + (pkg.resources ?? []).map((res: any) => res.name + ' ' + res.length + ' min').join(', '),
      'Admissions: ' + (pkg.admissions ?? []).map((adm: any) => adm.name + ' ' + (Number(adm.price) || 0).toFixed(2) + (adm.fixedPrice ? ' per booking' : ' each')).join(', '),
      'Deposit policies: ' + (pkg.publicDepositPolicy?.name ?? '-') + ' (public), ' + (pkg.venueDepositPolicy?.name ?? '-') + ' (venue)',
      pkg.ispartypackage ? 'Party package' : null
    ].filter(line => line != null).join('\n');
  }

  //How long the package runs, laid out as Package Edit's itinerary lays it out.
  private packageLength(pkg: any): number {
    const resources: any[] = pkg.resources ?? [];
    const session = resources.filter(res => res.inline).reduce((total, res) => total + (Number(res.length) || 0), 0);
    let offset = 0;
    let end = 0;

    resources.forEach(res => {
      const length = Number(res.length) || 0;
      let start: number;

      if (res.inline) {
        start = offset;
        offset += length;
      }
      else {
        start = res.startwhen == 'ATEND' ? session : res.startwhen == 'OFFSET' ? Number(res.startTimeOffset) || 0 : 0;
      }

      end = Math.max(end, start + length);
    });

    return end;
  }

  private bookingWindow(pkg: any): any {
    return this.compact({
      bookings_for_today: pkg.cannotmakebookingstoday ? 'not allowed' : 'allowed',
      minimum_notice: (Number(pkg.bookingscannotbemadewithinxminutes) || 0) > 0 ? this.minutesText(pkg.bookingscannotbemadewithinxminutes) : undefined,
      furthest_ahead: (Number(pkg.bookingscannotbemadeafterxminutes) || 0) > 0 ? this.daysAheadText(pkg.bookingscannotbemadeafterxminutes) : undefined,
      calendar_months_shown: pkg.maxcalendarmonths || 3
    });
  }

  private visibilityText(value: any): string {
    switch ((value ?? '').toString().toUpperCase()) {
      case 'NEITHER': return 'Hidden';
      case 'PUBLIC': return 'Public Bookings Only';
      case 'VENUE': return 'Preview/Venue Only';
      case 'BOTH': return 'Public and Venue Bookings';
      default: return (value ?? 'not set').toString();
    }
  }

  private minutesText(minutes: any): string {
    const total = Number(minutes) || 0;
    const days = Math.floor(total / 1440);
    const hours = Math.floor((total % 1440) / 60);
    const mins = total % 60;
    return [days > 0 ? days + ' days' : '', hours > 0 ? hours + ' hours' : '', mins > 0 ? mins + ' minutes' : ''].filter(part => part != '').join(' ') || '0 minutes';
  }

  private daysAheadText(minutes: any): string {
    const total = Number(minutes) || 0;
    return total > 0 ? Math.round(total / 1440) + ' days' : 'no limit';
  }

  private weekdayName(weekday: string): string {
    return weekday.charAt(0).toUpperCase() + weekday.substring(1);
  }

  private minutesOf(time: any): number {
    const match = /^(\d{1,2}):(\d{2})/.exec((time ?? '').toString());
    return match == null ? null : Number(match[1]) * 60 + Number(match[2]);
  }

  //Stored dates here are the day written down with a midnight time and a Z (wall-clock-as-Z),
  //so only the date part is read - turning them into a Date would let the timezone move them.
  private keyOfText(value: any): number {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec((value ?? '').toString());
    return match == null ? null : Number(match[1]) * 10000 + Number(match[2]) * 100 + Number(match[3]);
  }

  private keyOfDay(day: Date): number {
    return day.getFullYear() * 10000 + (day.getMonth() + 1) * 100 + day.getDate();
  }

  //dd/mm/yyyy from a stored date or a YYYY-MM-DD.
  private dayText(value: any): string {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec((value ?? '').toString());
    return match == null ? (value ?? '').toString() : match[3] + '/' + match[2] + '/' + match[1];
  }

  //A calendar day as it is saved: that day at midnight with a Z, as Package Edit and Time Set
  //Edit save a date picked on their calendars.
  private wallClockDay(day: Date): string {
    return day.getFullYear() + '-' + ('0' + (day.getMonth() + 1)).slice(-2) + '-' + ('0' + day.getDate()).slice(-2) + 'T00:00:00.000Z';
  }

  private ddMMyy(day: Date): string {
    return ('0' + day.getDate()).slice(-2) + ('0' + (day.getMonth() + 1)).slice(-2) + day.getFullYear().toString().slice(-2);
  }

  private parseDay(value: any): Date {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec((value ?? '').toString().trim());
    return match == null ? null : new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }

  private dayOrFail(value: any, name: string): Date {
    const day = this.parseDay(value);

    if (day == null) {
      throw new Error(name + ' must be YYYY-MM-DD.');
    }

    return day;
  }

  private compact(item: any): any {
    const result: any = {};

    Object.keys(item).forEach(key => {
      const value = item[key];
      const empty = value == null || value === '' || (Array.isArray(value) && value.length == 0) ||
        (typeof value == 'object' && !Array.isArray(value) && Object.keys(value).length == 0);

      if (!empty) {
        result[key] = value;
      }
    });

    return result;
  }

  //#endregion
}
